<template>
  <v-app>
    <v-container v-if="!user" fluid class="fill-height login-container">
      <v-row align="center" justify="center">
        <v-col cols="12" sm="6" md="3">
          <v-card class="elevation-12">
            <!-- Owner-branded on purpose. Nobody is authenticated yet, so
                 there is no user doc to resolve branding from and every
                 account sees this same screen — client accounts included. -->
            <v-card-text class="text-center pa-4">
              <v-img
                :src="maxLogo"
                alt="Cop Cam Max Logo"
                height="80"
                contain
              ></v-img>
            </v-card-text>
            <v-toolbar color="primary" dark flat>
              <v-toolbar-title>Cop Cam Max Dashboard Login</v-toolbar-title>
            </v-toolbar>
            <v-card-text>
              <p class="mb-4 text-center">Please sign in to access the dashboard.</p>
              
              <v-form @submit.prevent="handleEmailPasswordSignIn">
                <v-text-field
                  label="Email"
                  v-model="email"
                  variant="outlined"
                  density="compact"
                  class="mb-2"
                  @keydown.enter="handleEmailPasswordSignIn"
                  autofocus
                  autocomplete="username"
                ></v-text-field>

                <v-text-field
                  label="Password"
                  :type="showPassword ? 'text' : 'password'"
                  v-model="password"
                  variant="outlined"
                  density="compact"
                  @keydown.enter="handleEmailPasswordSignIn"
                  :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                  @click:append-inner="showPassword = !showPassword"
                  autocomplete="current-password"
                ></v-text-field>
              </v-form>
              <div class="text-right" style="margin-top: -10px; margin-bottom: 10px;">
                <v-btn
                  variant="text"
                  size="small"
                  class="text-capitalize"
                  @click="handleForgotPassword"
                >
                  Forgot Password?
                </v-btn>
              </div>
              <v-alert
                v-if="loginError"
                type="error"
                density="compact"
                class="mb-3"
                variant="tonal" 
              >
                {{ loginError }}
              </v-alert>
              <v-btn 
                @click="handleEmailPasswordSignIn" 
                color="primary"
                block
                class="mb-4"
              >
                Sign In
              </v-btn>
            
              <v-row align="center" class="my-2">
                <v-divider></v-divider>
                <span class="px-2 text-caption">OR</span>
                <v-divider></v-divider>
              </v-row>
            
              <v-btn color="red" @click="handleSignIn" block>
                <v-icon start icon="mdi-google"></v-icon>
                Sign In with Google
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <v-main v-else class="no-bottom-padding analytical-background">
      <v-navigation-drawer v-model="mobileNavOpen" temporary location="left">
        <v-list density="compact" nav>
          <v-list-item
            v-for="item in primaryNavItems"
            :key="item.view"
            :active="currentView === item.view"
            :prepend-icon="item.icon"
            :title="item.label"
            @click="selectMobileNavItem(item.view)"
          ></v-list-item>
        </v-list>
      </v-navigation-drawer>

      <v-container fluid class="px-2 pt-2 pb-0">
        <v-row class="mb-4 align-center" no-gutters>
          <v-col v-if="!mobile" class="d-flex align-center">
            <v-btn :color="currentView === 'records' ? 'primary' : 'blue-grey-lighten-4'" variant="elevated" @click="setView('records')" class="ml-2 zoom-on-hover nav-btn-compact"> <v-icon start icon="mdi-account"></v-icon>Records </v-btn>
            <v-btn :color="currentView === 'agencyResources' ? 'primary' : 'blue-grey-lighten-4'" variant="elevated" @click="setView('agencyResources')" class="ml-2 zoom-on-hover nav-btn-compact"><v-icon start icon="mdi-city"></v-icon>Agencies</v-btn>
            <v-btn :color="currentView === 'research' ? 'primary' : 'blue-grey-lighten-4'" variant="elevated" @click="setView('research')" class="ml-2 zoom-on-hover nav-btn-compact"><v-icon start icon="mdi-flask"></v-icon>Research</v-btn>
            <v-btn :color="currentView === 'docket' ? 'primary' : 'blue-grey-lighten-4'" variant="elevated" @click="setView('docket')" class="ml-2 zoom-on-hover nav-btn-compact"><v-icon start icon="mdi-calendar-clock"></v-icon>Docket</v-btn>
            <v-btn :color="currentView === 'notes' ? 'grey-darken-3' : 'blue-grey-lighten-4'" variant="elevated" @click="setView('notes')" class="ml-2 zoom-on-hover nav-btn-compact"><v-icon start icon="mdi-note-text-outline"></v-icon>Notes+</v-btn>
            <v-btn :color="currentView === 'analytics' ? 'grey-darken-3' : 'blue-grey-lighten-4'" variant="elevated" @click="setView('analytics')" class="ml-2 zoom-on-hover nav-btn-compact"><v-icon start icon="mdi-chart-bar"></v-icon>Analytics</v-btn>

            <v-btn :color="currentView === 'aiReports' ? 'grey-darken-3' : 'blue-grey-lighten-4'" variant="elevated" @click="setView('aiReports')" class="ml-2 zoom-on-hover nav-btn-compact"><v-icon start icon="mdi-robot-outline"></v-icon>AI Reports</v-btn>
            <v-btn :color="currentView === 'library' ? 'grey-darken-3' : 'blue-grey-lighten-4'" variant="elevated" @click="setView('library')" class="ml-2 zoom-on-hover nav-btn-compact"><v-icon start icon="mdi-bookshelf"></v-icon>Library</v-btn>

            <v-btn color="green-darken-2" variant="elevated" @click="openDialog()" v-show="currentView === 'records'" style="margin-left: 12px; min-width: 130px; --v-btn-height: 30px;" class="zoom-on-hover"><v-icon start icon="mdi-plus-circle"></v-icon>Record</v-btn>
            <v-btn color="green-darken-2" variant="elevated" @click="openAgencyResourceDialog()" v-show="currentView === 'agencyResources'" style="margin-left: 12px; min-width: 130px; --v-btn-height: 30px;" class="zoom-on-hover"><v-icon start icon="mdi-plus-circle"></v-icon>Agency</v-btn>
            <v-btn color="green-darken-2" variant="elevated" @click="openResearchDialog()" v-show="currentView === 'research'" style="margin-left: 12px; min-width: 130px; --v-btn-height: 30px;" class="zoom-on-hover"><v-icon start icon="mdi-plus-circle"></v-icon>Research</v-btn>
            <v-btn color="green-darken-2" variant="elevated" @click="openDocketAddDialog()" v-show="currentView === 'docket'" style="margin-left: 12px; min-width: 130px; --v-btn-height: 30px;" class="zoom-on-hover"><v-icon start icon="mdi-plus-circle"></v-icon>Item</v-btn>
          </v-col>

          <v-col v-else class="d-flex align-center mobile-nav-bar">
            <v-btn
              icon
              size="small"
              color="blue-grey-lighten-4"
              class="ml-1"
              aria-label="Open navigation menu"
              @click="mobileNavOpen = true"
            >
              <v-icon icon="mdi-menu"></v-icon>
            </v-btn>
            <span class="ml-2 text-subtitle-2 font-weight-medium">{{ currentNavLabel }}</span>

            <v-spacer></v-spacer>

            <v-btn color="green-darken-2" variant="elevated" icon size="small" @click="openDialog()" v-show="currentView === 'records'" class="zoom-on-hover" aria-label="Add Record"><v-icon icon="mdi-plus-circle"></v-icon></v-btn>
            <v-btn color="green-darken-2" variant="elevated" icon size="small" @click="openAgencyResourceDialog()" v-show="currentView === 'agencyResources'" class="zoom-on-hover" aria-label="Add Agency"><v-icon icon="mdi-plus-circle"></v-icon></v-btn>
            <v-btn color="green-darken-2" variant="elevated" icon size="small" @click="openResearchDialog()" v-show="currentView === 'research'" class="zoom-on-hover" aria-label="Add Research"><v-icon icon="mdi-plus-circle"></v-icon></v-btn>
            <v-btn color="green-darken-2" variant="elevated" icon size="small" @click="openDocketAddDialog()" v-show="currentView === 'docket'" class="zoom-on-hover" aria-label="Add Docket Item"><v-icon icon="mdi-plus-circle"></v-icon></v-btn>
          </v-col>

          <v-spacer v-if="!mobile"></v-spacer>

          <v-col cols="auto" class="d-flex justify-end align-center top-right-actions">
            
            <v-tooltip v-if="isAdminUser" text="Open Google Sheet" location="bottom">
              <template #activator="{ props }">
                <v-btn
                  icon
                  size="small"
                  v-bind="props"
                  href="https://docs.google.com/spreadsheets/d/1ciXB7ZmGIY8SOpCeaWhcA7SADeUyqyA5UdE4KCm6QOQ/edit?gid=0#gid=0"
                  target="_blank"
                  rel="noopener noreferrer"
                  color="blue-grey-lighten-4"
                  class="btn-outline-green zoom-on-hover"
                  aria-label="Open Google Sheet"
                >
                  <v-icon icon="mdi-google-spreadsheet" color="green"></v-icon>
                </v-btn>
              </template>
            </v-tooltip>

            <v-tooltip v-if="isAdminUser" text="Open Google Doc" location="bottom">
              <template #activator="{ props }">
                <v-btn
                  icon
                  size="small"
                  v-bind="props"
                  href="https://docs.google.com/document/d/1Cg150Lw3p3c4IkDNj-o49XHZCjb0kVdply6F7fU8prg/edit?tab=t.1xiqcyry28j4#heading=h.t57vxgujfbja"
                  target="_blank"
                  rel="noopener noreferrer"
                  color="blue-grey-lighten-4"
                  class="btn-outline-blue zoom-on-hover"
                  aria-label="Open Google Doc"
                >
                  <v-icon icon="mdi-file-document" color="#4285F4"></v-icon>
                </v-btn>
              </template>
            </v-tooltip>

            <v-tooltip v-if="isAdminUser" text="Create New Folder" location="bottom">
              <template #activator="{ props }">
                <v-btn
                  icon
                  size="small"
                  v-bind="props"
                  href="createfolder://"
                  color="blue-grey-lighten-4"
                  class="btn-outline-black zoom-on-hover"
                  aria-label="Create New Folder"
                >
                  <v-icon icon="mdi-folder-plus-outline"></v-icon>
                </v-btn>
              </template>
            </v-tooltip>
            
            <v-tooltip text="Export CSV" location="bottom">
              <template #activator="{ props }">
                <div v-bind="props" class="d-inline-block">
                  <v-btn
                    icon
                    size="small"
                    @click="generateCSV()"
                    :disabled="csvExportButtonDisabled"
                    v-if="!['notes', 'analytics', 'aiReports', 'library'].includes(currentView)"
                    aria-label="Export CSV"
                    color="blue-grey-lighten-4"
                    class="btn-outline-black zoom-on-hover"
                  >
                    <v-icon icon="mdi-download"></v-icon>
                  </v-btn>
                </div>
              </template>
            </v-tooltip>

            <v-tooltip text="Export All Records for AI Analysis" location="bottom">
              <template #activator="{ props }">
                <div v-bind="props" class="d-inline-block">
                  <v-btn
                    icon
                    size="small"
                    @click="exportForAI()"
                    aria-label="Export for AI Analysis"
                    color="blue-grey-lighten-4"
                    class="zoom-on-hover btn-outline-purple"
                  >
                    <v-icon icon="mdi-robot-outline" color="purple-darken-2"></v-icon>
                  </v-btn>
                </div>
              </template>
            </v-tooltip>
            
            <v-menu offset-y>
              <template #activator="{ props: menuProps }">
                <v-tooltip location="bottom">
                  <template #activator="{ props: tooltipProps }">
                    <v-btn
                      color="blue-grey-lighten-4"
                      v-bind="{ ...menuProps, ...tooltipProps }"
                      class="zoom-on-hover"
                      :class="{ 'pa-0 account-avatar-btn': showOwnerAvatar }"
                      icon
                      size="small"
                    >
                      <img
                        v-if="showOwnerAvatar"
                        :src="logoSrc"
                        alt="Account"
                        style="width: 100%; height: 100%; object-fit: cover; border-radius: 50%;"
                        @error="ownerAvatarFailed = true"
                      >
                      <v-icon v-else icon="mdi-account-circle"></v-icon>
                    </v-btn>
                  </template>
                  <span>Account Menu ({{ viewedAccountLabel }})</span>
                </v-tooltip>
              </template>

              <v-list density="compact">
                
                <v-list-item>
                  <v-list-item-title class="font-weight-bold">
                    {{ viewedAccountLabel }}
                  </v-list-item-title>
                  <v-list-item-subtitle v-if="viewedAccountEmail">
                    {{ viewedAccountEmail }}
                  </v-list-item-subtitle>
                </v-list-item>
                <v-divider></v-divider>

                <div v-if="isAdminUser">
                  <v-list-subheader v-if="clientAccounts.length">Client Accounts</v-list-subheader>
                  <div class="px-3 pt-4 pb-3" style="overflow: visible;">
                    <v-select
                      v-model="selectedUserId"
                      :items="accountSwitcherItems"
                      item-props
                      label="Viewing as"
                      density="compact"
                      variant="outlined"
                      hide-details
                      @click.stop
                    ></v-select>
                  </div>
                  <v-divider></v-divider>
                </div>
                
                <v-list-item @click="handleSignOut" base-color="red">
                  <template #prepend>
                    <v-icon icon="mdi-logout"></v-icon>
                  </template>
                  <v-list-item-title>Log Out</v-list-item-title>
                </v-list-item>

                <v-divider></v-divider>
                <div class="px-4 py-2 text-caption text-grey">
                  <div>v{{ appVersion }}</div>
                  <div>Built: {{ buildDate }}</div>
                </div>
              </v-list>
            </v-menu>
          </v-col>
        </v-row>
        
        <RecordsViewSkeleton v-if="currentView === 'records' && recordsLoading" />
        
        <div v-if="visitedViews.has('records')" v-show="currentView === 'records' && !recordsLoading">
          <RecordsView
          :records="displayedRequests"
          :all-records="allRequests"
          :next-due-dates="nextDueDatesMap"
          :loading="recordsLoading"
          :agencyOptions="agencyNameOptions"
          :stateOptions="dynamicAgencyStateFilterOptions"
          :stageOptions="stageOptions"
          :agencies="agencyResourcesData"
          :sortBy="sortBy"
          :dashboard-title="dashboardTitle"
          :docket-items="docketItems"
          
          v-model:globalSearch="toolbarSearchQuery"

          v-model:searchQuery="searchQuery"
          v-model:agencySearchQuery="agencySearchQuery"
          v-model:stageFilter="stageFilter"
          v-model:stageExcludeFilter="stageExcludeFilter"
          v-model:agencyStateFilter="agencyStateFilter"
          v-model:recordRatingFilter="recordRatingFilter" 
          v-model:recordRatingMode="recordRatingMode"
          v-model:statusFilter="statusFilter"
          v-model:typeFilter="typeFilter"
          @update:sortBy="handleSortChange"
          @edit-record="editRecord"
          @edit-agency="editAgencyFromRecord"
          @update-docket-item="updateDocketItem"
          @reset-docket-item="openResetDialog"
          @request-add-docket="openDocketAddDialog"
          />
        </div>

        <div v-if="visitedViews.has('agencyResources')" v-show="currentView === 'agencyResources'">
          <AgenciesView
          :agencies="displayedAgencyResources"
          :all-requests="allRequests" 
          :loading="agenciesLoading"
          :agencyOptions="agencyNameOptions"
          :areaOptions="areaOptions"
          :stateOptions="dynamicAgencyStateFilterOptions"
          :dashboard-title="dashboardTitle"
          v-model:agencySearchQuery="agencySearchQuery"
          v-model:areaSearchQuery="areaSearchQuery"
          v-model:agencyStateFilter="agencyStateFilter"
          v-model:agencyStatusFilter="agencyStatusFilter"
          v-model:agencySortBy="agencySortBy"
          v-model:globalSearch="toolbarSearchQuery"
          @edit-agency="editAgencyResource"
          @edit-record="editRecord"  @refresh="handleRefresh"
          />
        </div>

        <div v-if="visitedViews.has('research')" v-show="currentView === 'research'">
          <ResearchView
          :research="displayedResearchRecords"
          :loading="researchLoading"
          :sourceOptions="sourceOptions"
          :stateOptions="dynamicAgencyStateFilterOptions"
          :dashboard-title="dashboardTitle"
          v-model:sourceSearchQuery="sourceSearchQuery"
          v-model:agencyStateFilter="agencyStateFilter"
          v-model:researchSortBy="researchSortBy"
          v-model:globalSearch="toolbarSearchQuery"
          @edit-research="editResearchEntry"
          @refresh="handleRefresh"
          />
        </div>

        <div v-if="visitedViews.has('analytics')" v-show="currentView === 'analytics'">
          <AnalyticsView
          :records="allRequests"
          :agencies="agencyResourcesData"
          :stage-options="stageOptions"
          :getAgencyDisplayName="getAgencyDisplayName"
          :is-analyzing="isAnalyzing"
          :ai-data="aiAnalysisData"
          @request-analysis="generateAIAnalysis"
          @edit-record="editRecord"
          @edit-agency="editAgencyFromRecord"
          @navigate-to-agencies="handleNavigateToAgencies"
          />
        </div>

        <div v-if="visitedViews.has('notes')" v-show="currentView === 'notes'">
          <NotesView
          :notes="notes"
          :active-main-tabs="activeNoteTabs"
          @open-note-dialog="openNoteDialog"
          />
        </div>

        <div v-if="visitedViews.has('docket')" v-show="currentView === 'docket'">
          <DocketView
          ref="docketViewRef"
          :docket-items="docketItems"
          :agencies="agencyResourcesData"
          :all-requests="allRequests"
          :loading="docketLoading"
          :dashboard-title="dashboardTitle"
          :get-agency-display-name="getAgencyDisplayName"
          @add-item="openDocketAddDialog"
          @update-item="updateDocketItem"
          @delete-item="deleteDocketItem"
          @edit-record="editRecord"
          @edit-agency="editAgencyFromRecord"
          @reset-item="openResetDialog"
          @update-agency-data="saveAgencyResource"
          @empty-trash="handleEmptyTrash"
          />
        </div>

        <div v-if="visitedViews.has('aiReports')" v-show="currentView === 'aiReports'">
          <AIReportsView
          :records="allRequests"
          :agencies="agencyResourcesData"
          :stage-options="stageOptions"
          :get-agency-display-name="getAgencyDisplayName"
          :dynamic-state-options="dynamicAgencyStateFilterOptions"
          v-model:searchQuery="aiReportSearchQuery"
          v-model:historicalWindow="aiHistoricalWindow"
          v-model:reportType="aiReportType"
          v-model:agingWindow="aiAgingWindow"
          v-model:minRating="aiMinRating"
          v-model:stateFilter="aiStateFilter"
          @edit-record="editRecord"
          @edit-agency="editAgencyFromRecord"
          @add-docket-item="handleAIReportAddDocket"
          @navigate-to-records="openRecordsForAgencyInNewTab"
          />
        </div>

        <div v-if="visitedViews.has('library')" v-show="currentView === 'library'">
          <LibraryView
          :records="allRequests"
          :agencies="agencyResourcesData"
          :initial-agency="libraryInitialAgency"
          @edit-agency="editAgencyFromRecord"
          @edit-record="editRecord"
          @navigate-to-records="openRecordsForAgencyInNewTab"
          @initial-agency-consumed="libraryInitialAgency = ''"
          />
        </div>

        <RecordEditDialog
          v-if="form"
          v-model="dialog"
          :item="form"
          :is-saving="isSavingRecord"
          :agency-options="agencyNameOptions"
          :stage-options="stageOptions"
          :how-found-options="howFoundOptions"
          :all-agencies="agencyResourcesData"
          :is-admin-user="isAdminUser"
          :library-agency-keys="libraryAgencyKeys"
          @save="handleSaveRecord"
          @delete="openDeleteConfirmDialog('record')"
          @close="dialog = false"
          @request-new-agency="handleRequestNewAgency"
          @view-agency="handleViewAgency"
          @open-in-library="openLibraryForAgencyInNewTab"
        />

        <AgencyEditDialog
          v-if="agencyForm"
          v-model="agencyDialog"
          :item="agencyForm"
          :is-saving="isSavingAgency"
          :agency-options="agencyNameOptions"
          :area-options="areaOptions" :all-requests="allRequests"
          :stage-options="stageOptions"
          :get-agency-display-name="getAgencyDisplayName"
          @save="saveAgencyResource"
          @delete="openDeleteConfirmDialog('agency')"
          :library-agency-keys="libraryAgencyKeys"
          @view-records="viewAssociatedRecords"
          @open-in-library="openLibraryForAgencyInNewTab"
          @close="agencyDialog = false"
        />

        <ResearchDialog
          v-if="researchForm"
          v-model="researchDialog"
          :item="researchForm"
          :is-saving="isSavingResearch"
          :source-options="sourceOptions"
          :all-state-options="allStateOptions"
          :charges-options="chargesOptions"
          @save="saveResearchEntry"
          @delete="openDeleteConfirmDialog('research')"
          @close="researchDialog = false"
        />

        <DocketAddDialog
          v-if="docketAddDialog"
          v-model="docketAddDialog"
          :agency-options="agencyNameOptions"
          :agencies="agencyResourcesData"
          :loading="isSavingDocket"
          :initial-agency="docketAddInitialAgency"
          :existing-docket-agencies="activeDocketAgencyNames"
          @save="saveNewDocketItem"
          @view-agency="handleViewAgency"   
        />

        <DocketResetDialog
          v-if="docketResetDialog"
          v-model="docketResetDialog"
          :loading="isResettingDocket"
          :current-notes="itemToReset?.notes"
          :agency-name="itemToReset?.agencyName"
          :current-due-date="itemToReset ? nextDueDatesMap.get((itemToReset.agencyName || '').toLowerCase().trim()) || null : null"
          :current-base-duration="itemToReset?.baseDuration || '1M'"
          :has-action-flag="!!(itemToReset?.extensions?.some(e => String(e).startsWith('Action!')))"
          @save="confirmResetDocketItem"
        />
        
        <v-dialog v-model="noteDialog" max-width="70%" persistent>
           <v-card>
            <v-card-title :style="{ backgroundColor: activeNote.color }" class="note-modal-title d-flex align-center">
              <span class="mr-4">{{ activeNote.title }}</span>
              <v-spacer></v-spacer>
              <v-btn icon @click="closeNoteDialog" class="modal-close-button-note">
                <v-icon color="white" icon="mdi-close"></v-icon>
              </v-btn>
            </v-card-title>
            
            <v-card-text class="pa-0 note-modal-tabs-container">
              <v-tabs v-model="activeDialogTab" class="bordered-tabs">
                <v-tab v-for="(tab, index) in activeNote.content" :key="index" :value="index">
                  <div @dblclick.stop="startRename(index)" class="d-flex align-center">
                    <v-text-field
                      v-if="renamingTab === index"
                      v-model="tab.title"
                      density="compact"
                      hide-details
                      variant="underlined"
                      autofocus
                      @blur="finishRename"
                      @keydown.enter="finishRename"
                      @click.stop
                      class="tab-rename-field"
                    ></v-text-field>
                    <span v-else class="tab-title-text">{{ tab.title }}</span>
                    <v-btn
                      v-if="activeDialogTab === index && renamingTab !== index"
                      icon
                      variant="text"
                      size="x-small"
                      @click.stop="startRename(index)"
                      class="ml-1"
                    >
                      <v-icon size="x-small" icon="mdi-pencil"></v-icon>
                    </v-btn>
                  </div>
                  <v-btn icon variant="text" size="x-small" @click.stop="deleteTab(index)" class="ml-1">
                    <v-icon size="small" icon="mdi-close"></v-icon>
                  </v-btn>
                </v-tab>
                <v-btn icon variant="text" @click="addTab">
                  <v-icon icon="mdi-plus"></v-icon>
                </v-btn>
              </v-tabs>
            </v-card-text>

            <v-card-text class="dialog-content-scrollable pa-0">
              <NoteEditor v-if="activeNote.content && activeNote.content[activeDialogTab]" v-model="activeNote.content[activeDialogTab].content" />
            </v-card-text>

            <v-card-actions class="pa-4">
              <div class="text-caption text-grey-darken-1">
                <span v-if="saveStatus === 'unsaved'">Unsaved changes</span>
                <span v-if="saveStatus === 'saving'">Saving...</span>
                <span v-if="saveStatus === 'saved' && lastSavedTimestamp">
                  Last saved: {{ formatTimestamp(lastSavedTimestamp) }}
                </span>
                <span v-if="saveStatus === 'error'" class="text-red">Error saving</span>
              </div>
              
              <v-spacer></v-spacer>
              
              <v-btn 
                color="blue" 
                variant="elevated" 
                size="large"
                class="px-6"
                @click="saveNote"
              >
                <span class="mr-2" style="font-size: 1.3rem;">💾</span>
                Save & Close
              </v-btn>

              <v-btn 
                color="grey" 
                variant="text" 
                size="large"
                @click="closeNoteDialog"
              >
                <v-icon start>mdi-close</v-icon>
                Cancel
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        
        <v-dialog v-model="stageRecordsDialog" max-width="90%" persistent>
             <v-card>
            <v-card-title>
              Records in Stage: {{ stageRecordsTitle }} ({{ stageRecords.length }})
              <v-btn icon @click="stageRecordsDialog = false" class="modal-close-button">
                <v-icon color="white">mdi-close</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text>
              <v-data-table
                height="70vh"
                :headers="associatedRecordsHeaders"
                :items="stageRecords"
                class="elevation-1 styled-table"
                density="comfortable"
                fixed-header
                items-per-page="100"
              >
                <template #item.name="{ item }">
                  <a href="#" @click.prevent="editRecordFromStats(item)" class="table-link">{{ capitalizeWords(item.name) }}</a>
                </template>
                 <template #item.displayDate="{ item }">
                    {{ item.displayDate ? formatDate(item.displayDate) : '' }}
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-dialog>

        <RecordsListModal
          v-model="associatedRecordsDialog"
          :title="`Associated Records for ${agencyNameForAssociatedRecords}`"
          :agency-name="agencyNameForAssociatedRecords"
          :records="associatedRecords"
          :headers="RECORDS_MODAL_HEADERS"
          :get-agency-display-name="getAgencyDisplayName"
          @edit-record="editRecord($event); associatedRecordsDialog = false"
          @edit-agency="editAgencyFromRecord($event); associatedRecordsDialog = false"
        />

        <v-dialog v-model="unsavedChangesDialog" max-width="500px" persistent>
          <v-card>
            <v-card-title class="text-h6 font-weight-bold">Unsaved Changes</v-card-title>
            <v-card-text>You have unsaved changes. How would you like to proceed?</v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text @click="handleUnsavedDialog('cancel')">Cancel</v-btn>
              <v-btn color="red" text @click="handleUnsavedDialog('discard')">Discard Changes</v-btn>
              <v-btn color="blue" variant="flat" @click="handleUnsavedDialog('save')">Save & Close</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-dialog v-model="deleteConfirmDialog" max-width="500px" persistent>
          <v-card>
            <v-card-title class="text-h6 red--text text--darken-2">Confirm Deletion</v-card-title>
            <v-card-text>This action is permanent (Undo available for 5s). Are you sure you want to delete this record?</v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="grey" @click="deleteConfirmDialog = false">Cancel</v-btn>
              <v-btn color="red" @click="initiateDelete()" :loading="isDeleting">Delete</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-dialog v-model="newAgencyConfirmDialog" max-width="500px" persistent>
          <v-card>
            <v-card-title class="text-h6">Create New Agency?</v-card-title>
            <v-card-text>
              The agency "{{ newAgencyName }}" does not exist. Do you want to create a new agency resource with this name?
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="grey" @click="cancelNewAgency()">Cancel</v-btn>
              <v-btn color="blue" @click="confirmAndSaveNewAgency()">Create & Save Record</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-container>
    </v-main>
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      location="bottom center"
    >
      {{ snackbar.message }}
      <template #actions>
        <v-btn
          v-if="snackbar.undoAction"
          color="white"
          variant="text"
          @click="undoDelete(); snackbar.show = false;"
        >
          Undo
        </v-btn>
        <v-btn
          v-if="snackbar.ghostFilterAction"
          color="white"
          variant="text"
          @click="viewGhostedRecords()"
        >
          View Ghosted
        </v-btn>
        <v-btn
          color="white"
          variant="text"
          @click="snackbar.show = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>

  </v-app>
</template>

<script setup>
import { ref, nextTick, computed, onMounted, onUnmounted, reactive, watch, defineAsyncComponent, shallowRef } from 'vue';
import { useDisplay } from 'vuetify';
import { stringify } from 'csv-stringify/browser/esm/sync';
import * as XLSX from 'xlsx';
import {
  collection,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  doc,
  deleteDoc,
  serverTimestamp,
  query,
  where,
  setDoc,
  collectionGroup,
  onSnapshot,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { auth, authHelpers, getFirebaseServices } from '../firebase';
import RecordsViewSkeleton from './RecordsViewSkeleton.vue';
// maxLogo is still used directly by the pre-auth login card; the toolbar logo
// comes from the signed-in account's profile via the branding resolver.
import maxLogo from '@/assets/logo.png';
import { resolveAccountBranding, isClientAccount, accountLabel } from '@/utils/clientAccounts';
import { getFunctions, httpsCallable } from 'firebase/functions';

// __APP_VERSION__/__BUILD_DATE__ are injected by webpack.DefinePlugin (vue.config.cjs)
// from package.json and the build timestamp — never hardcode these.
const appVersion = __APP_VERSION__;
const buildDate = new Date(__BUILD_DATE__).toLocaleDateString('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

// --- Lazy Load Dialog Components ---
const RecordEditDialog = defineAsyncComponent(() => import('./RecordEditDialog.vue'));
const AgencyEditDialog = defineAsyncComponent(() => import('./AgencyEditDialog.vue'));
const ResearchDialog = defineAsyncComponent(() => import('./ResearchDialog.vue'));
const DocketAddDialog = defineAsyncComponent(() => import('./DocketAddDialog.vue'));
const DocketResetDialog = defineAsyncComponent(() => import('./DocketResetDialog.vue'));

// --- Lazy Load View Components ---
const RecordsView = defineAsyncComponent(() => import('./RecordsView.vue'));
const AgenciesView = defineAsyncComponent(() => import('./AgenciesView.vue'));
const ResearchView = defineAsyncComponent(() => import('./ResearchView.vue'));
const AnalyticsView = defineAsyncComponent(() => import('./AnalyticsView.vue'));
const NotesView = defineAsyncComponent(() => import('./NotesView.vue'));
const DocketView = defineAsyncComponent(() => import('./DocketView.vue'));
const NoteEditor = defineAsyncComponent(() => import('./NoteEditor.vue'));

// NEW AI REPORTS IMPORT
const AIReportsView = defineAsyncComponent(() => import('./AIReportsView.vue'));
const LibraryView = defineAsyncComponent(() => import('./LibraryView.vue'));
// Warm the other views' JS chunks once the first screen has painted, so the
// first visit to each tab doesn't wait on a network fetch.
const prefetchViews = () => {
  [() => import('./AgenciesView.vue'), () => import('./ResearchView.vue'), () => import('./AnalyticsView.vue'),
   () => import('./NotesView.vue'), () => import('./DocketView.vue'), () => import('./AIReportsView.vue'),
   () => import('./LibraryView.vue')].forEach(load => load().catch(() => {}));
};
if (typeof window !== 'undefined') {
  (window.requestIdleCallback || ((fn) => setTimeout(fn, 2000)))(prefetchViews);
}
const RecordsListModal = defineAsyncComponent(() => import('./RecordsListModal.vue'));

// Import shared formatters and utils
import {
  formatDate,
  formatTimestamp,
  capitalizeWords,
  formatAgencyName,
  formatAndLinkUrls,
  getStageEmojiAndLabel,
  getAgencyDisplayName,
  csvSafeCell,
  getRecordType,
  deepEqual,
} from '@/utils/formatters.js';
import { logError } from '@/utils/logError.js';
import { STAGE_OPTIONS, RECORDS_MODAL_HEADERS } from '@/utils/constants.js';
import { resolveGhostThreshold } from '@/utils/ghostThresholds.js';

// Admin status comes from a custom auth claim (set via set-admin-claim.js),
// read in the onAuth callback below. No admin email ships in the bundle.
const isAdminUser = ref(false);

// Import Docket logic for sanitization and calculation
import { useDocketLogic } from '@/composables/useDocketLogic';
const { sanitizeDocketItem, getCalculatedDate } = useDocketLogic();

import { useDirtyTracking } from '@/composables/useDirtyTracking';
const { markClean: markNoteClean, isDirty: isNoteDirty } = useDirtyTracking();

import { useUndoableAction } from '@/composables/useUndoableAction';
const deleteUndo = useUndoableAction();

import { isLibraryRecord } from '@/composables/useReportStatus.js';

// --- SCRIPT STATE ---
const user = ref(null);
const email = ref('');
const password = ref('');
const showPassword = ref(false); 
const loginError = ref(''); 

// shallowRef: these hold thousand-row arrays that are only ever replaced
// wholesale by filterData — deep reactivity on every row/field is pure overhead.
const displayedRequests = shallowRef([]);
const allRequests = shallowRef([]);
const dialog = ref(false);
const howFoundOptions = ref([]);
const recordsLoading = ref(false);
const agenciesLoading = ref(false);
const researchLoading = ref(false);
const isSavingRecord = ref(false);
const isSavingAgency = ref(false);
const isSavingResearch = ref(false);
const isDeleting = ref(false);
const form = ref(null);
const agencyForm = ref(null);
const researchForm = ref(null);
const currentView = ref('records');
// Views mount on first visit, then stay mounted (v-show) so switching back is
// instant instead of rebuilding the view and re-running its setup/fetches.
const visitedViews = reactive(new Set(['records']));
watch(currentView, (v) => {
  visitedViews.add(v);
  if (v === 'notes' && firebaseServices.value?.db) ensureNotes(firebaseServices.value.db);
  // Views that size themselves from window height can't measure while hidden.
  nextTick(() => window.dispatchEvent(new Event('resize')));
}, { immediate: true });
// Agency handed over by the record/agency modals' "Open Library" button (see
// openLibraryForAgencyInNewTab); LibraryView consumes it once and selects it.
const libraryInitialAgency = ref('');
// Lowercased names of agencies that would appear in Library's agency list —
// the record and agency modals only offer "Open Library" for these, so the button
// never lands on an empty right column. Same lowercase key Library's own
// canonicalAgency() groups on.
const libraryAgencyKeys = computed(() => new Set(
  allRequests.value.filter(isLibraryRecord).map((r) => getAgencyDisplayName(r).toLowerCase()).filter(Boolean)
));
const { mobile } = useDisplay();
const mobileNavOpen = ref(false);
const primaryNavItems = [
  { view: 'records', label: 'Records', icon: 'mdi-account' },
  { view: 'agencyResources', label: 'Agencies', icon: 'mdi-city' },
  { view: 'research', label: 'Research', icon: 'mdi-flask' },
  { view: 'docket', label: 'Docket', icon: 'mdi-calendar-clock' },
  { view: 'notes', label: 'Notes+', icon: 'mdi-note-text-outline' },
  { view: 'analytics', label: 'Analytics', icon: 'mdi-chart-bar' },
  { view: 'aiReports', label: 'AI Reports', icon: 'mdi-robot-outline' },
  { view: 'library', label: 'Library', icon: 'mdi-bookshelf' },
];
const currentNavLabel = computed(() => primaryNavItems.find(item => item.view === currentView.value)?.label ?? '');
// Browser tab title follows the active page, e.g. "Library · CCM".
watch(currentNavLabel, (label) => {
  document.title = label ? `${label} · CCM` : 'CCM Dashboard';
}, { immediate: true });
function selectMobileNavItem(viewName) {
  setView(viewName);
  mobileNavOpen.value = false;
}
const agencyResourcesData = shallowRef([]);
const displayedAgencyResources = shallowRef([]);
const agencyDialog = ref(false);
const associatedRecordsDialog = ref(false);
const associatedRecords = ref([]);
const agencyNameForAssociatedRecords = ref('');
// Dormant "Records in Stage" dialog — template + editRecordFromStats() reference
// these but the refs were dropped in a refactor; declare them to stop render
// warnings. Dialog is never opened (nothing sets stageRecordsDialog = true).
const stageRecordsDialog = ref(false);
const stageRecordsTitle = ref('');
const stageRecords = ref([]);
const researchRecords = shallowRef([]);
const displayedResearchRecords = shallowRef([]);
const researchDialog = ref(false);
const chargesOptions = ref([]);
const sourceOptions = ref([]);
const notes = ref([]);
const noteDialog = ref(false);
const activeNote = ref({ id: '', title: '', content: [], color: '' });
const isAnalyzing = ref(false);
const aiAnalysisData = ref(null);
let unsubscribeRecords = null;
let unsubscribeAgencies = null;
let unsubscribeResearch = null;
let unsubscribeNotes = null;
const activeDialogTab = ref(0);
const renamingTab = ref(null);
const saveStatus = ref('saved');
const lastSavedTimestamp = ref(null);
const deleteConfirmDialog = ref(false);
const itemToDelete = ref(null);
const deleteType = ref(null);
const newAgencyConfirmDialog = ref(false);
const newAgencyName = ref('');
const snackbar = ref({ show: false, message: '', color: '', timeout: 3000, undoAction: false, ghostFilterAction: false });
const firebaseServices = ref(null);
const unsavedChangesDialog = ref(false);
const activeNoteTabs = reactive({ general: 0, agency: 0, research: 0, extra: 0});
const allUsers = ref([]);
const selectedUserId = ref(null);
// users/{uid} document for the signed-in account — drives branding via
// @/utils/clientAccounts. Null until the doc loads; the resolver falls back
// to owner branding in that window.
const myProfile = ref(null);
const docketViewRef = ref(null);

// --- SEARCH STATE ---
const toolbarSearchQuery = ref('');

// --- MEMORY FOR SEARCH STATES ---
const viewSearchStates = reactive({
  records: '',
  agencyResources: '',
  research: '',
  docket: '',
  notes: '',
  analytics: '',
  aiReports: '' // New entry for AI Reports view
});

// --- DOCKET STATE ---
const docketItems = shallowRef([]);
const docketLoading = ref(false);
const docketAddDialog = ref(false);
const isSavingDocket = ref(false);
let unsubscribeDocket = null;
const docketResetDialog = ref(false);
const itemToReset = ref(null);
const isResettingDocket = ref(false);
const docketAddInitialAgency = ref(''); 

// --- Filter & Sort State ---
const stageFilter = ref([]);
const stageExcludeFilter = ref([]); 
const searchQuery = ref('');
const agencySearchQuery = ref('');
const areaSearchQuery = ref('');
const sourceSearchQuery = ref('');
const agencyStateFilter = ref('All');
const agencyStatusFilter = ref([]); 
const recordRatingFilter = ref(0);
const recordRatingMode = ref('min'); 
const statusFilter = ref([]);
const typeFilter = ref(['footage', 'informational']);

// --- AI REPORTS FILTER STATE MEMORY ---
const aiReportSearchQuery = ref('');
const aiHistoricalWindow = ref('365'); 
const aiReportType = ref('dormant');
const aiAgingWindow = ref(90); // Default 3 months
// Empty = no filter (everyone shown, Cool included) — matches CLAUDE.md:
// Cool stays visible in Dormant/Acquisition recommendations, just ranked
// lowest. The old default silently excluded it (the "Exclude Cool" threshold
// preset); switching to a checklist made that mismatch visible, so it's
// fixed here rather than carried forward as the new default.
const aiMinRating = ref([]);
const aiStateFilter = ref('All'); 

const sortBy = ref([{ key: 'displayDate', order: 'desc' }, { key: 'timestamp', order: 'desc' }]);
const agencySortBy = ref([{ key: 'hotStatus', order: 'desc' }, { key: 'requests', order: 'desc' }]);
const researchSortBy = ref([{ key: 'dateReviewDone', order: 'desc' }]);

// Per-view filter memory, persisted to localStorage — same load/watch-and-save
// pattern as DocketView's sort persistence. Saved values are merged ON TOP OF
// defaults (per view block) rather than trusting the parsed object outright,
// so a stale/partial localStorage entry from an older app version can't leave
// a key missing and break the destructuring restores in setView() below.
const VIEW_FILTERS_STORAGE_KEY = 'viewFilters_preference';
const DEFAULT_VIEW_FILTERS = {
  records: {
      searchQuery: '',
      agencySearchQuery: '',
      stageFilter: [],
      stageExcludeFilter: [],
      agencyStateFilter: 'All',
      recordRatingFilter: 0,
      recordRatingMode: 'min',
      statusFilter: [],
      typeFilter: ['footage', 'informational'],
      sortBy: [{ key: 'displayDate', order: 'desc' }, { key: 'timestamp', order: 'desc' }]
  },
  agencyResources: {
    agencySearchQuery: '',
    areaSearchQuery: '',
    agencyStateFilter: 'All',
    agencyStatusFilter: [],
    agencySortBy: [{ key: 'hotStatus', order: 'desc' }, { key: 'requests', order: 'desc' }]
  },
  research: { sourceSearchQuery: '', agencyStateFilter: 'All', researchSortBy: [{ key: 'dateReviewDone', order: 'desc' }] },
  // Memory block to hold state when navigating away from AI Reports
  aiReports: {
    aiReportSearchQuery: '',
    aiHistoricalWindow: '365',
    aiReportType: 'dormant',
    aiAgingWindow: 90,
    aiMinRating: [],
    aiStateFilter: 'All'
  }
};
// aiMinRating used to be a single "at least this good" threshold (-1/0/1/2).
// It's now a checklist of individual rating tiers (array of hotStatus
// values), matching Library's Agency Rating filter. A value saved by an
// older build is still a bare number, so it's mapped to the equivalent
// tier list here rather than silently reset to "show everything" on upgrade.
function normalizeAiMinRating(value) {
  if (Array.isArray(value)) return value;
  switch (value) {
    case 2: return [2];
    case 1: return [1, 2];
    case 0: return [0, 1, 2];
    default: return []; // legacy -1 ("Any Rating") or unrecognized — no filter
  }
}
function loadPersistedViewFilters() {
  try {
    const saved = JSON.parse(localStorage.getItem(VIEW_FILTERS_STORAGE_KEY));
    if (!saved) return structuredClone(DEFAULT_VIEW_FILTERS);
    return {
      records: { ...DEFAULT_VIEW_FILTERS.records, ...saved.records },
      agencyResources: { ...DEFAULT_VIEW_FILTERS.agencyResources, ...saved.agencyResources },
      research: { ...DEFAULT_VIEW_FILTERS.research, ...saved.research },
      aiReports: {
        ...DEFAULT_VIEW_FILTERS.aiReports,
        ...saved.aiReports,
        aiMinRating: normalizeAiMinRating(
          saved.aiReports?.aiMinRating ?? DEFAULT_VIEW_FILTERS.aiReports.aiMinRating
        ),
      },
    };
  } catch {
    return structuredClone(DEFAULT_VIEW_FILTERS);
  }
}

const viewFilters = reactive(loadPersistedViewFilters());
watch(viewFilters, (newVal) => {
  try { localStorage.setItem(VIEW_FILTERS_STORAGE_KEY, JSON.stringify(newVal)); } catch {}
}, { deep: true });

// setView() below only restores viewFilters into the live filter refs on a
// view SWITCH — 'records' is the default active view from page load, so it
// never goes through that restore path. Hydrate it once here to pick up
// whatever was persisted.
({
    searchQuery: searchQuery.value,
    agencySearchQuery: agencySearchQuery.value,
    stageFilter: stageFilter.value,
    stageExcludeFilter: stageExcludeFilter.value,
    agencyStateFilter: agencyStateFilter.value,
    recordRatingFilter: recordRatingFilter.value,
    recordRatingMode: recordRatingMode.value,
    statusFilter: statusFilter.value,
    typeFilter: typeFilter.value,
    sortBy: sortBy.value
} = viewFilters.records);

// --- COMPUTED PROPERTIES ---
const viewAsUserId = computed(() => selectedUserId.value || (user.value ? user.value.uid : null));
// Branding follows the account that is SIGNED IN, not the one being viewed —
// impersonating a client account to check their data shouldn't reskin the app.
const accountBranding = computed(() => resolveAccountBranding(myProfile.value));
const dashboardTitle = computed(() => accountBranding.value.dashboardTitle);
const logoSrc = computed(() => accountBranding.value.logo);
// The channel art replaces the Account button's generic icon for the owner's
// own login only; client accounts (and anyone else) keep the plain icon.
const OWNER_EMAIL = 'naclwater@gmail.com';
const ownerAvatarFailed = ref(false);
const showOwnerAvatar = computed(() =>
  !ownerAvatarFailed.value
  && user.value?.email?.toLowerCase() === OWNER_EMAIL
  && !isClientAccount(myProfile.value)
);

// Admin account switcher: the owner's own account first, then client accounts
// alphabetically. Client rows show displayName with the email as a subtitle.
const ownAccounts = computed(() => allUsers.value.filter(u => !isClientAccount(u)));
const clientAccounts = computed(() =>
  allUsers.value
    .filter(u => isClientAccount(u))
    .sort((a, b) => accountLabel(a).localeCompare(accountLabel(b)))
);
const accountSwitcherItems = computed(() =>
  [...ownAccounts.value, ...clientAccounts.value].map(u => ({
    value: u.id,
    title: accountLabel(u),
    subtitle: u.displayName ? u.email : undefined,
  }))
);
const viewedAccount = computed(() => allUsers.value.find(u => u.id === selectedUserId.value));
const viewedAccountLabel = computed(() =>
  viewedAccount.value ? accountLabel(viewedAccount.value) : (user.value?.email || 'Account')
);
// Only shown when the label above is a display name, so the email still
// appears somewhere rather than being replaced by it.
const viewedAccountEmail = computed(() =>
  viewedAccount.value?.displayName ? viewedAccount.value.email : ''
);
const dynamicAgencyStateFilterOptions = computed(() => { const states = new Set([...agencyResourcesData.value.map(item => item.state).filter(Boolean),...researchRecords.value.map(item => item.state).filter(Boolean)]); const sortedStates = Array.from(states).sort(); return [{ label: 'All', value: 'All' }, ...sortedStates.map(state => ({ label: state, value: state }))]; });
const allStateOptions = computed(() => { const states = new Set([...agencyResourcesData.value.map(item => item.state).filter(Boolean),...researchRecords.value.map(item => item.state).filter(Boolean)]); return Array.from(states).sort(); });
const agencyNameOptions = computed(() => { const agencyRecencyMap = new Map(); for (const record of allRequests.value) { const agencyName = getAgencyDisplayName(record); if (agencyName && record.timestamp) { const existingTimestamp = agencyRecencyMap.get(agencyName); const currentTimestamp = record.timestamp?.toDate ? record.timestamp.toDate() : null; if (currentTimestamp && (!existingTimestamp || currentTimestamp > existingTimestamp)) { agencyRecencyMap.set(agencyName, currentTimestamp); } } } for (const agency of agencyResourcesData.value) { const agencyName = getAgencyDisplayName(agency); if (agencyName && !agencyRecencyMap.has(agencyName)) { agencyRecencyMap.set(agencyName, new Date(0)); } } const sortedAgencyNames = Array.from(agencyRecencyMap.entries()).sort((a, b) => b[1] - a[1]).map(entry => entry[0]); return sortedAgencyNames; });
const areaOptions = computed(() => { const areas = new Set(agencyResourcesData.value.map(item => item.area).filter(Boolean)); return Array.from(areas).sort(); });
// PERF: O(1) agency lookup by lowercased display name. Rebuilds only when the
// agency list changes — replaces repeated linear .find() scans in hot paths.
const agencyByNameMap = computed(() => {
  const map = new Map();
  agencyResourcesData.value.forEach(a => {
    const key = getAgencyDisplayName(a).toLowerCase();
    if (key && !map.has(key)) map.set(key, a);
  });
  return map;
});
const csvExportButtonDisabled = computed(() => { 
  if (currentView.value === 'records') return !displayedRequests.value.length; 
  if (currentView.value === 'agencyResources') return !displayedAgencyResources.value.length; 
  if (currentView.value === 'research') return !displayedResearchRecords.value.length;
  if (currentView.value === 'docket') return !docketItems.value.length; 
  return true; // Disables the global CSV button for notes, analytics, and aiReports
});
const associatedRecordsHeaders = [ { title: 'Name', key: 'name' }, { title: 'Request Date', key: 'displayDate' }, { title: 'Stage', key: 'stage' }, { title: 'How Found', key: 'howFound' }, { title: 'Cost', key: 'cost' }, { title: 'Subject Matter', key: 'subjectMatter' }, { title: 'Misc.', key: 'misc' }, { title: 'Update', key: 'update' }, { title: 'Other Info', key: 'otherInfo' } ];
const stageOptions = STAGE_OPTIONS;

// Lowercase list of agency names with at least one active (non-archived, non-trash) docket entry
const activeDocketAgencyNames = computed(() => {
  const names = new Set();
  docketItems.value.forEach(item => {
    if (!item.archived && !item.trash) {
      const key = (item.agencyName || '').toLowerCase().trim();
      if (key) names.add(key);
    }
  });
  return Array.from(names);
});

const nextDueDatesMap = computed(() => {
  const map = new Map();
  if (!docketItems.value.length) return map;

  docketItems.value.forEach(item => {
    if (item.trash || item.archived) return;
    const rawName = item.agencyName || item.Agency || '';
    const agencyKey = rawName.toLowerCase().trim();
    if (!agencyKey) return;

    const calculatedDate = getCalculatedDate(item);
    const isAction = item.extensions?.some(e => String(e).startsWith('Action!'));

    const currentBest = map.get(agencyKey);

    let update = false;
    if (!currentBest) {
      update = true;
    } else if (isAction && !currentBest.isAction) {
      update = true;
    } else if (isAction === currentBest.isAction) {
      if (calculatedDate < currentBest.date) {
        update = true;
      }
    }

    if (update) {
      map.set(agencyKey, {
        date: calculatedDate,
        isAction: isAction,
        displayText: isAction ? 'ACTION' : formatDate(calculatedDate)
      });
    }
  });
  return map;
});

// --- FUNCTIONS AND METHODS ---
async function handleEmailPasswordSignIn() {
  loginError.value = ''; 
  if (!email.value || !password.value) { loginError.value = 'Please enter both email and password.'; return; }
  try { await authHelpers.signInWithEmail(email.value, password.value); } catch (error) { console.error("Email sign-in error:", error.code); loginError.value = 'Invalid email or password. Please try again.'; }
}

async function handleForgotPassword() {
  loginError.value = ''; 
  if (!email.value) { loginError.value = 'Please enter your email address first.'; return; }
  try { await authHelpers.sendPasswordReset(email.value); showSnackbar('Password reset email sent. Please check your inbox.', 'success', 5000); } catch (error) { console.error("Forgot password error:", error); loginError.value = 'Error sending password reset email. Please try again.'; }
}

function parseSearchWithNegation(query) { const includeTerms = []; const excludeTerms = []; const terms = query.toLowerCase().split(/\s+/).filter(Boolean); for (const term of terms) { if (term.startsWith('-') && term.length > 1) { excludeTerms.push(term.substring(1)); } else { includeTerms.push(term); } } return { includeTerms, excludeTerms }; }

async function undoDelete() {
  const pending = deleteUndo.consume();
  if (!pending || !pending.item?.id || !firebaseServices.value?.db) return;
  const { type, item } = pending;
  let collectionName = '';
  if (type === 'record') collectionName = 'CCMSheetsDATA';
  else if (type === 'agency') collectionName = 'AgencyInfo';
  else if (type === 'research') collectionName = 'researchRecords';
  else if (type === 'docket') collectionName = 'docketItems';
  try {
    // item is the raw Firestore doc data captured at delete time (real
    // Timestamps, no UI-mapped fields like displayDate/requestedOn), so it
    // can be restored verbatim.
    const docId = item.id;
    const dataToRestore = { ...item };
    delete dataToRestore.id;
    await setDoc(doc(firebaseServices.value.db, collectionName, docId), dataToRestore);
    showSnackbar('Item restored', 'info');
  } catch (error) {
    console.error("Error restoring item:", error);
    logError('undoDelete', error); showSnackbar('Couldn\'t undo the delete. The item may need to be re-added manually.', 'error');
  }
}

function openNoteDialog(note, tabIndex) {
  activeNote.value = JSON.parse(JSON.stringify(note));
  activeDialogTab.value = tabIndex !== undefined ? tabIndex : 0;
  unsavedChangesDialog.value = false;
  markNoteClean(activeNote.value.content);
  noteDialog.value = true;
}

function closeNoteDialog() {
  if (activeNote.value) {
    const hasChanged = isNoteDirty(activeNote.value.content);
    if (hasChanged && saveStatus.value !== 'saving') {
      unsavedChangesDialog.value = true;
      return;
    }
  }
  noteDialog.value = false;
}

function handleUnsavedDialog(action) { unsavedChangesDialog.value = false; if (action === 'save') saveNote(); else if (action === 'discard') { noteDialog.value = false; activeNote.value = { id: '', title: '', content: [], color: '' }; } }

onMounted(() => {
  window.addEventListener('beforeunload', handleAutoSave);
  // New-tab jumps: from Library to Records (openRecordsForAgencyInNewTab;
  // Records is already the default view, so only the filters need applying),
  // or from the record modal to Library (openLibraryForAgencyInNewTab,
  // ?view=library). Either way the params are stripped so a refresh doesn't
  // keep re-applying them.
  const startParams = new URLSearchParams(window.location.search);
  const agencyParam = startParams.get('agency');
  if (agencyParam && startParams.get('view') === 'library') {
    libraryInitialAgency.value = agencyParam;
    setView('library');
    window.history.replaceState(null, '', window.location.pathname);
  } else if (agencyParam) {
    viewRecordsForAgency(agencyParam);
    window.history.replaceState(null, '', window.location.pathname);
  }
  authHelpers.onAuth(async (authUser) => {
    user.value = authUser;
    if (authUser) {
      const { db: firestoreDb } = await getFirebaseServices();
      firebaseServices.value = { db: firestoreDb, auth };
      selectedUserId.value = authUser.uid; 
      const userRef = doc(firestoreDb, 'users', authUser.uid);
      await setDoc(userRef, { email: authUser.email, lastLogin: serverTimestamp() }, { merge: true });
      // Branding (title/logo/channel) lives on this doc — see
      // @/utils/clientAccounts. A read failure is non-fatal: the resolver
      // falls back to owner branding.
      try {
        const profileSnap = await getDoc(userRef);
        myProfile.value = profileSnap.exists() ? profileSnap.data() : null;
      } catch (error) {
        console.error('Could not read account profile:', error);
        logError('readAccountProfile', error);
        myProfile.value = null;
      }
      try {
        const tokenResult = await authUser.getIdTokenResult();
        isAdminUser.value = tokenResult.claims.admin === true;
      } catch (error) {
        console.error('Could not read auth claims:', error);
        logError('readAuthClaims', error);
        isAdminUser.value = false;
      }
      if (isAdminUser.value) {
        try {
          const userListSnapshot = await getDocs(collection(firebaseServices.value.db, 'users'));
          allUsers.value = userListSnapshot.docs
            .filter(doc => { const data = doc.data(); return data.email && typeof data.email === 'string' && data.lastLogin; }) 
            // Full doc data, so the switcher can show displayName and tell
            // client accounts apart from the owner's without a second read.
            .map(doc => ({ id: doc.id, ...doc.data() }));
          const adminInList = allUsers.value.some(u => u.id === authUser.uid);
          if (!adminInList) {
             const adminDoc = userListSnapshot.docs.find(d => d.id === authUser.uid);
             if (adminDoc && adminDoc.data().email && typeof adminDoc.data().email === 'string' && adminDoc.data().lastLogin) {
                allUsers.value.unshift({ id: authUser.uid, email: authUser.email });
             }
          }
        } catch (error) { console.error("Error fetching user list:", error); logError('fetchUserList', error); if (authUser.email) { allUsers.value = [{ id: authUser.uid, email: authUser.email }]; } }
      } else { allUsers.value = [{ id: authUser.uid, email: authUser.email, ...(myProfile.value || {}) }]; }
      loadFirestoreData(firestoreDb); loadAgencyResourcesData(firestoreDb); loadResearchData(firestoreDb); resetNotes(firestoreDb); loadDocketData(firestoreDb);
    } else {
      if (unsubscribeRecords) unsubscribeRecords(); if (unsubscribeAgencies) unsubscribeAgencies(); if (unsubscribeResearch) unsubscribeResearch(); if (unsubscribeNotes) unsubscribeNotes(); notesStarted = false; if (unsubscribeDocket) unsubscribeDocket();
      allRequests.value = []; displayedRequests.value = []; agencyResourcesData.value = []; displayedAgencyResources.value = []; researchRecords.value = []; displayedResearchRecords.value = []; notes.value = []; docketItems.value = [];
      allUsers.value = []; selectedUserId.value = null; isAdminUser.value = false; myProfile.value = null;
    }
  });
});

onUnmounted(() => { window.removeEventListener('beforeunload', handleAutoSave); deleteUndo.clear(); if (unsubscribeRecords) unsubscribeRecords(); if (unsubscribeAgencies) unsubscribeAgencies(); if (unsubscribeResearch) unsubscribeResearch(); if (unsubscribeNotes) unsubscribeNotes(); if (unsubscribeDocket) unsubscribeDocket(); });

function handleSignIn() { authHelpers.signIn(); }
function handleSignOut() { authHelpers.logOut(); }
function showSnackbar(message, color = 'success', timeout = 3000, undo = false, ghostFilter = false) { snackbar.value = { message, color, timeout, undoAction: undo, ghostFilterAction: ghostFilter, show: true }; }

// One-click affordance for the auto-ghost sweep snackbar — jumps to Records
// filtered to the stage it just set, instead of telling the user to search
// "auto-ghosted" manually.
function viewGhostedRecords() {
  setView('records');
  stageFilter.value = ['Ghosted'];
  snackbar.value.show = false;
}

// Library's "View this agency's records" jump (LibraryAgencyPanel.vue) —
// same setView()-plus-direct-ref-mutation pattern as viewGhostedRecords
// above. Clears the stage filters and resets the type filter to "everything"
// rather than trying to guess a single stage that matches both of Library's
// own agency-level signals (Footage Slots is about footage records, Info
// Pending is about informational ones) — narrowing further from here is a
// one-click follow-up the operator can do themselves once they see the list.
function viewRecordsForAgency(agencyName) {
  if (!agencyName) return;
  setView('records');
  // Clear every other Records filter too — the new tab hydrates them from
  // localStorage, so a leftover name search, state, rating or status filter
  // would otherwise silently hide part of this agency's records.
  searchQuery.value = '';
  toolbarSearchQuery.value = '';
  agencySearchQuery.value = agencyName;
  stageFilter.value = [];
  stageExcludeFilter.value = [];
  agencyStateFilter.value = 'All';
  recordRatingFilter.value = 0;
  recordRatingMode.value = 'min';
  statusFilter.value = [];
  typeFilter.value = ['footage', 'informational'];
}

// Library's button opens the agency's records in a NEW tab so the Library
// selection stays put. The app has no per-view URLs, so the agency travels as
// a ?agency= query param, consumed once on load in onMounted (which then
// strips it, so a refresh doesn't keep re-applying the filter).
function openRecordsForAgencyInNewTab(agencyName) {
  if (!agencyName) return;
  const url = new URL(window.location.origin + '/');
  url.searchParams.set('agency', agencyName);
  window.open(url.toString(), '_blank');
}

// The reverse trip: the record and agency modals' "Open Library" buttons open Library
// in a NEW tab (so the modal and any unsaved edits stay put) with the
// record's agency selected in the right column.
function openLibraryForAgencyInNewTab(agencyName) {
  if (!agencyName) return;
  const url = new URL(window.location.origin + '/');
  url.searchParams.set('view', 'library');
  url.searchParams.set('agency', agencyName);
  window.open(url.toString(), '_blank');
}

function openDialog() { 
  if (!firebaseServices.value?.db) return; 
  form.value = { 
    id: null, 
    name: '', 
    agencyName: '', 
    stage: 'Pending', 
    requestedOn: '', 
    quoteDate: '', 
    paidDate: '', 
    receivedDate: '',
    bullpenDate: '',
    cost: '', 
    howFound: '', 
    subjectMatter: '', 
    misc: '', 
    update: '', 
    otherInfo: '' 
  }; 

  dialog.value = true; 
}

// Shared by editRecord() (initial open) and loadFirestoreData()'s live
// listener (re-normalizing the currently-open record after an external
// write, e.g. create_folder.py's or the email automation's stage/rating/
// date write-back) — both need the exact same record-to-form shape.
function normalizeRecordForForm(record) {
  const recordCopy = { ...record };
  recordCopy.agencyName = getAgencyDisplayName(record);
  delete recordCopy.Agency;
  return {
    ...recordCopy,
    requestedOn: record.displayDate ? new Date(record.displayDate).toISOString().split('T')[0] : '',
    quoteDate: record.quoteDate ? new Date(record.quoteDate).toISOString().split('T')[0] : '',
    paidDate: record.paidDate ? new Date(record.paidDate).toISOString().split('T')[0] : '',
    receivedDate: record.receivedDate ? new Date(record.receivedDate).toISOString().split('T')[0] : '',
    bullpenDate: record.bullpenDate ? new Date(record.bullpenDate).toISOString().split('T')[0] : ''
  };
}

function editRecord(record) {
  form.value = normalizeRecordForForm(record);
  dialog.value = true;
}

function editRecordFromStats(record) { stageRecordsDialog.value = false; editRecord(record); }
function handleViewAgency(agencyName) { const agencyResource = agencyByNameMap.value.get(agencyName.toLowerCase()); if (agencyResource) { editAgencyResource(agencyResource); } }

// Returns the current time-of-day as HH:MM:SS Eastern wall-clock digits,
// suitable for re-tagging (not converting) onto a date as literal UTC
// digits — the exact same trick create-ars-record.md / create-video-record.md
// use for requestedOnFixed. Needed because the app previously used a FIXED
// noon (T12:00:00Z) for every new/date-changed record regardless of actual
// save time, while the commands use the real Eastern clock — two different
// conventions for "what time-of-day to store" on the same field meant
// same-day sort order between app-created and command-created records was
// essentially a coin flip (whichever happened to be written with the later
// time-of-day digits sorted last, with no relation to true creation order).
// Bringing the app in line with the commands' real-time convention (rather
// than the reverse) preserves the commands' own same-day tiebreaker fix.
function getEasternTimeOfDayDigits() {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
  }).formatToParts(new Date());
  const get = (type) => parts.find(p => p.type === type)?.value ?? '00';
  let hour = get('hour');
  if (hour === '24') hour = '00'; // some engines/locales render midnight as "24"
  return `${hour}:${get('minute')}:${get('second')}`;
}

async function handleSaveRecord(recordData, options = {}) { 
  if (!firebaseServices.value?.db) return; 
  const dataToSave = { ...recordData }; 
  const agencyName = getAgencyDisplayName(dataToSave).trim(); 
  // FIXED: agency check happens BEFORE the spinner starts, and can be skipped
  // when the agency was just created (snapshot may not have synced yet).
  if (!options.skipAgencyCheck) {
    const agencyExists = agencyNameOptions.value.some(opt => opt.toLowerCase() === agencyName.toLowerCase()); 
    if (!agencyExists) { newAgencyName.value = agencyName; newAgencyConfirmDialog.value = true; return; } 
  }
  isSavingRecord.value = true; 
  dataToSave.agencyName = agencyName; 
  delete dataToSave.Agency; 
  const recordId = dataToSave.id; 
  delete dataToSave.id; 
  
  // FIX: Strip undefined values and synthetic UI properties to prevent Firestore crash.
  // Deny-list (not a whitelist) so legitimate new fields aren't silently dropped.
  // agencyDetails/_cost/_daysSinceQuote are added by AIReportsView's Open/Quote report
  // maps; recordType is UI-only classification. (1.1)
  delete dataToSave.displayDate;
  delete dataToSave.agencyDetails;
  delete dataToSave._cost;
  delete dataToSave._daysSinceQuote;
  delete dataToSave.recordType;
  Object.keys(dataToSave).forEach(key => {
    if (dataToSave[key] === undefined) {
      delete dataToSave[key];
    }
  });

  // FIX: cost can arrive as a string (form initializes cost: ''), which breaks
  // toLocaleString currency formatting on read. Coerce to a number on save;
  // keep '' for the empty case, matching the display sites' emptiness check. (1.8)
  if (dataToSave.cost !== undefined) {
    dataToSave.cost = dataToSave.cost === '' || dataToSave.cost === null
      ? ''
      : parseFloat(dataToSave.cost);
  }

  if (dataToSave.requestedOn) {
    // Preserve the original requestedOnFixed's time-of-day precision when the
    // calendar date hasn't actually changed. The Claude Code commands
    // (create-ars-record, create-video-record) stamp requestedOnFixed with
    // real Eastern time-of-day specifically to break same-day sort ties —
    // recomputing it to noon UTC on EVERY save (even edits that never touch
    // the date field, like changing Stage) was silently destroying that
    // precision. Only recompute fresh when the date genuinely changes.
    const originalRecord = recordId ? allRequests.value.find(r => r.id === recordId) : null;
    const originalDateStr = originalRecord?.displayDate
      ? new Date(originalRecord.displayDate).toISOString().split('T')[0] : null;
    if (originalRecord?.requestedOnFixed && dataToSave.requestedOn === originalDateStr) {
      dataToSave.requestedOnFixed = originalRecord.requestedOnFixed; // unchanged date — keep exact original Timestamp
    } else {
      // Use the user-chosen CALENDAR DATE (from the date field) combined with
      // the REAL current time-of-day (not a fixed noon) — same "real now,
      // re-tagged as UTC digits" trick the commands use. This guarantees a
      // record created/dated "today" always sorts by true creation order
      // relative to same-day records from either the app or a command,
      // instead of noon (app) vs. real-time (commands) racing arbitrarily.
      dataToSave.requestedOnFixed = Timestamp.fromDate(new Date(dataToSave.requestedOn + 'T' + getEasternTimeOfDayDigits() + 'Z')); // new record or date genuinely changed
    }
  }
  if (dataToSave.quoteDate) dataToSave.quoteDate = Timestamp.fromDate(new Date(dataToSave.quoteDate + 'T12:00:00Z')); 
  if (dataToSave.paidDate) dataToSave.paidDate = Timestamp.fromDate(new Date(dataToSave.paidDate + 'T12:00:00Z')); 
  if (dataToSave.receivedDate) dataToSave.receivedDate = Timestamp.fromDate(new Date(dataToSave.receivedDate + 'T12:00:00Z')); 
  if (dataToSave.bullpenDate) dataToSave.bullpenDate = Timestamp.fromDate(new Date(dataToSave.bullpenDate + 'T12:00:00Z')); 
  // checkDate was previously saved as a raw YYYY-MM-DD string — the only one
  // of the five date fields not converted to a Timestamp. This mismatched
  // the two create-* commands' CRITICAL RULE, which writes checkDate (along
  // with quoteDate/paidDate/receivedDate/bullpenDate) as a noon-UTC
  // Timestamp. Read-side code already tolerates both types defensively
  // (see loadFirestoreData's checkDate mapping), so this is a safe,
  // backward-compatible change: old string-valued records keep working
  // untouched, new saves now match the commands' convention.
  if (dataToSave.checkDate) dataToSave.checkDate = Timestamp.fromDate(new Date(dataToSave.checkDate + 'T12:00:00Z')); 
  
  dataToSave.timestamp = serverTimestamp(); 
  dataToSave.userId = viewAsUserId.value; 
  // Optimistic: the Firestore SDK applies the write to its local cache (and
  // fires the snapshot listener) the moment it is issued, so the table already
  // shows the edit. Don't make the user sit through the server round trip --
  // close the modal now and confirm (or roll back) when the server answers.
  let slowNotice = null;
  try {
    const writePromise = recordId
      ? updateDoc(doc(firebaseServices.value.db, 'CCMSheetsDATA', recordId), dataToSave)
      : addDoc(collection(firebaseServices.value.db, 'CCMSheetsDATA'), dataToSave);
    dialog.value = false;
    isSavingRecord.value = false;
    slowNotice = setTimeout(() => showSnackbar('Still saving to the server…', 'warning'), 10000);
    await writePromise;
    showSnackbar(recordId ? 'Record updated successfully' : 'Record added successfully');
  } catch (error) {
    console.error("Error saving record:", error);
    logError('saveRecord', error);
    // Put the user's edits back in front of them -- the modal already closed.
    form.value = recordData;
    dialog.value = true;
    showSnackbar("Couldn't save the record. Check your connection and try again — your changes are still in the form.", 'error');
  } finally {
    if (slowNotice) clearTimeout(slowNotice);
    isSavingRecord.value = false;
  }
}

function handleRequestNewAgency(recordData) { form.value = recordData; newAgencyName.value = recordData.agencyName; newAgencyConfirmDialog.value = true; }
function openAgencyResourceDialog() { if (!firebaseServices.value?.db) return; agencyForm.value = { id: null, agencyName: '', state: '', area: '', agencyContact: '', email: '', court: '', contactInfo: '', miscInfo: '' }; agencyDialog.value = true; }
function editAgencyResource(agency) { agencyForm.value = { ...agency }; agencyDialog.value = true; }
function editAgencyFromRecord(record) { const agencyName = getAgencyDisplayName(record).toLowerCase(); const agencyResource = agencyByNameMap.value.get(agencyName); if (agencyResource) { editAgencyResource(agencyResource); } else { agencyForm.value = { id: null, agencyName: getAgencyDisplayName(record) }; agencyDialog.value = true; } }
async function saveAgencyResource(agencyData) {
  if (!firebaseServices.value?.db) return;
  isSavingAgency.value = true;
  const dataToSave = { ...agencyData };
  const agencyId = dataToSave.id;
  delete dataToSave.id;
  // Strip synthetic keys added by AgenciesView's augmentedAgencies before saving (1.1)
  delete dataToSave.requests;
  delete dataToSave.pending;
  dataToSave.timestamp = serverTimestamp();
  dataToSave.userId = viewAsUserId.value;
  const newName = dataToSave.agencyName ? dataToSave.agencyName.trim() : '';
  try {
    if (agencyId) {
      const originalAgency = agencyResourcesData.value.find(a => a.id === agencyId);
      const oldName = originalAgency ? (originalAgency.agencyName || '').trim() : '';
      if (oldName && newName && oldName !== newName) {
        const batch = writeBatch(firebaseServices.value.db);
        let updateCount = 0;
        batch.update(doc(firebaseServices.value.db, 'AgencyInfo', agencyId), dataToSave);
        allRequests.value.filter(r => getAgencyDisplayName(r) === oldName).forEach(record => { batch.update(doc(firebaseServices.value.db, 'CCMSheetsDATA', record.id), { agencyName: newName }); updateCount++; });
        researchRecords.value.filter(r => (r.agencyName || '').trim() === oldName).forEach(resRecord => { batch.update(doc(firebaseServices.value.db, 'researchRecords', resRecord.id), { agencyName: newName }); updateCount++; });
        docketItems.value.filter(d => (d.agencyName || '').trim() === oldName).forEach(docItem => { batch.update(doc(firebaseServices.value.db, 'docketItems', docItem.id), { agencyName: newName }); updateCount++; });
        await batch.commit();
        showSnackbar(`Agency updated and ${updateCount} associated records renamed.`);
      } else {
        await updateDoc(doc(firebaseServices.value.db, 'AgencyInfo', agencyId), dataToSave);
        showSnackbar('Agency updated successfully');
      }
    } else {
      await addDoc(collection(firebaseServices.value.db, 'AgencyInfo'), dataToSave);
      showSnackbar('Agency added successfully');
    }
    agencyDialog.value = false;
  } catch (error) { console.error("Error saving agency:", error); showSnackbar('Error saving agency', 'error'); } 
  finally { isSavingAgency.value = false; }
}
function getTodayLocalISOString() { const date = new Date(); const timezoneOffset = date.getTimezoneOffset() * 60000; return (new Date(date - timezoneOffset)).toISOString().split('T')[0]; };
function openResearchDialog() { if (!firebaseServices.value?.db) return; researchForm.value = { id: null, parentId: null, agencyName: '', state: '', area: '', source: '', charges: '', dateRangeStart: '', dateRangeEnd: '', dateReviewDone: getTodayLocalISOString(), noRequestsMade: '', nextSteps: '', notes: '' }; researchDialog.value = true; }
function editResearchEntry(entry) { researchForm.value = { ...entry, dateReviewDone: entry.dateReviewDone ? new Date(entry.dateReviewDone).toISOString().split('T')[0] : '', dateRangeStart: entry.dateRangeStart ? new Date(entry.dateRangeStart).toISOString().split('T')[0] : '', dateRangeEnd: entry.dateRangeEnd ? new Date(entry.dateRangeEnd).toISOString().split('T')[0] : '' }; researchDialog.value = true; }
async function saveResearchEntry(researchData) { if (!firebaseServices.value?.db) return; isSavingResearch.value = true; const researchToSave = { ...researchData }; if (researchToSave.dateReviewDone) researchToSave.dateReviewDone = Timestamp.fromDate(new Date(researchToSave.dateReviewDone + 'T12:00:00Z')); if (researchToSave.dateRangeStart) researchToSave.dateRangeStart = Timestamp.fromDate(new Date(researchToSave.dateRangeStart + 'T12:00:00Z')); if (researchToSave.dateRangeEnd) researchToSave.dateRangeEnd = Timestamp.fromDate(new Date(researchToSave.dateRangeEnd + 'T12:00:00Z')); researchToSave.timestamp = serverTimestamp(); researchToSave.userId = viewAsUserId.value; try { if (researchToSave.id) { await updateDoc(doc(firebaseServices.value.db, 'researchRecords', researchToSave.id), researchToSave); showSnackbar('Research entry updated successfully'); } else { delete researchToSave.id; await addDoc(collection(firebaseServices.value.db, 'researchRecords'), researchToSave); showSnackbar('Research entry added successfully'); } researchDialog.value = false; } catch (error) { console.error("Error saving research entry:", error); showSnackbar('Error saving research entry', 'error'); } finally { isSavingResearch.value = false; } }
async function saveNote() {
  if (!viewAsUserId.value || !activeNote.value.id || !firebaseServices.value?.db) return;
  saveStatus.value = 'saving';
  try {
    const noteRef = doc(firebaseServices.value.db, 'users', viewAsUserId.value, 'notes', activeNote.value.id);
    
    await setDoc(noteRef, { content: activeNote.value.content }, { merge: true });

    lastSavedTimestamp.value = new Date();
    saveStatus.value = 'saved';
    markNoteClean(activeNote.value.content);
    showSnackbar('Note saved successfully!');
    noteDialog.value = false;
  } catch (error) {
    console.error("Error saving note: ", error);
    saveStatus.value = 'error';
    logError('saveNote', error); showSnackbar('Couldn\'t save the note. It may have been deleted in another tab — copy your text, then refresh and try again.', 'error');
  }
}
async function handleAutoSave() { if (!viewAsUserId.value || !activeNote.value.id || !firebaseServices.value?.db || !noteDialog.value || !isNoteDirty(activeNote.value.content)) return; saveStatus.value = 'saving'; try { const noteRef = doc(firebaseServices.value.db, 'users', viewAsUserId.value, 'notes', activeNote.value.id); await updateDoc(noteRef, { content: activeNote.value.content }); lastSavedTimestamp.value = new Date(); saveStatus.value = 'saved'; markNoteClean(activeNote.value.content); } catch (error) { console.error("Auto-save failed: ", error); saveStatus.value = 'error'; } }
function addTab() { if (!firebaseServices.value?.db) return; if (activeNote.value && Array.isArray(activeNote.value.content)) { const newTabIndex = activeNote.value.content.length; activeNote.value.content.push({ title: `Tab ${newTabIndex + 1}`, content: '' }); activeDialogTab.value = newTabIndex; } }
function deleteTab(index) { if (!firebaseServices.value?.db) return; if (activeNote.value && Array.isArray(activeNote.value.content) && activeNote.value.content.length > 1) { activeNote.value.content.splice(index, 1); if (activeDialogTab.value >= index) { activeDialogTab.value = Math.max(0, activeDialogTab.value - 1); } } else { showSnackbar("Cannot delete the last tab.", "warning"); } }
function startRename(index) { renamingTab.value = index; }
function finishRename() { renamingTab.value = null; }


function setView(viewName) { 
  if (currentView.value === viewName) return; 
  
  // 1. SAVE CURRENT SEARCH STATE BEFORE SWITCHING
  viewSearchStates[currentView.value] = toolbarSearchQuery.value;

  const oldView = currentView.value; 
  if (viewFilters[oldView]) { 
    if (oldView === 'records') {
      viewFilters.records = {
          searchQuery: searchQuery.value,
          agencySearchQuery: agencySearchQuery.value,
          stageFilter: stageFilter.value,
          stageExcludeFilter: stageExcludeFilter.value,
          agencyStateFilter: agencyStateFilter.value,
          recordRatingFilter: recordRatingFilter.value,
          recordRatingMode: recordRatingMode.value,
          statusFilter: statusFilter.value,
          typeFilter: typeFilter.value,
          sortBy: sortBy.value
      };
    } else if (oldView === 'agencyResources') { 
      viewFilters.agencyResources = { 
        agencySearchQuery: agencySearchQuery.value, 
        areaSearchQuery: areaSearchQuery.value, 
        agencyStateFilter: agencyStateFilter.value, 
        agencyStatusFilter: agencyStatusFilter.value, 
        agencySortBy: agencySortBy.value 
      }; 
    } else if (oldView === 'research') { 
      viewFilters.research = { 
        sourceSearchQuery: sourceSearchQuery.value, 
        agencyStateFilter: agencyStateFilter.value, 
        researchSortBy: researchSortBy.value 
      }; 
    } else if (oldView === 'aiReports') {
      viewFilters.aiReports = { 
        aiReportSearchQuery: aiReportSearchQuery.value, 
        aiHistoricalWindow: aiHistoricalWindow.value,
        aiReportType: aiReportType.value,
        aiAgingWindow: aiAgingWindow.value,
        aiMinRating: aiMinRating.value,
        aiStateFilter: aiStateFilter.value
      };
    } 
  }
  
  // 2. SWITCH TO THE NEW VIEW
  currentView.value = viewName; 
  const newView = currentView.value; 
  
  // 3. RESTORE SEARCH STATE FOR THE NEW VIEW
  toolbarSearchQuery.value = viewSearchStates[newView] || '';

  // Trigger Filter immediately to reflect restored state
  if (newView === 'records') debounceFilterData();
  else if (newView === 'agencyResources') debounceFilterAgencyResources();
  else if (newView === 'research') debounceFilterResearchRecords();

  if (viewFilters[newView]) { 
    if (newView === 'records') {
      ({
          searchQuery: searchQuery.value,
          agencySearchQuery: agencySearchQuery.value,
          stageFilter: stageFilter.value,
          stageExcludeFilter: stageExcludeFilter.value = [],
          agencyStateFilter: agencyStateFilter.value,
          recordRatingFilter: recordRatingFilter.value,
          recordRatingMode: recordRatingMode.value,
          statusFilter: statusFilter.value,
          typeFilter: typeFilter.value = ['footage', 'informational'],
          sortBy: sortBy.value
      } = viewFilters.records);
    } else if (newView === 'agencyResources') { 
      ({ 
        agencySearchQuery: agencySearchQuery.value, 
        areaSearchQuery: areaSearchQuery.value, 
        agencyStateFilter: agencyStateFilter.value, 
        agencyStatusFilter: agencyStatusFilter.value, 
        agencySortBy: agencySortBy.value 
      } = viewFilters.agencyResources); 
    } else if (newView === 'research') { 
      ({ 
        sourceSearchQuery: sourceSearchQuery.value, 
        agencyStateFilter: agencyStateFilter.value, 
        researchSortBy: researchSortBy.value 
      } = viewFilters.research); 
    } else if (newView === 'aiReports') {
      ({ 
        aiReportSearchQuery: aiReportSearchQuery.value, 
        aiHistoricalWindow: aiHistoricalWindow.value,
        aiReportType: aiReportType.value,
        aiAgingWindow: aiAgingWindow.value,
        aiMinRating: aiMinRating.value,
        aiStateFilter: aiStateFilter.value
      } = viewFilters.aiReports);
    }
  } 
}

function handleSortChange(newSortBy) { sortBy.value = newSortBy; }

async function handleRefresh() { 
  if (!firebaseServices.value?.db) return; 
  const db = firebaseServices.value.db; 
  if (currentView.value === 'records') { 
    if(unsubscribeRecords) unsubscribeRecords(); 
    loadFirestoreData(db); 
    searchQuery.value = ''; 
    agencySearchQuery.value = ''; 
    stageFilter.value = []; 
    stageExcludeFilter.value = [];
    agencyStateFilter.value = 'All';
    recordRatingFilter.value = 0; 
    recordRatingMode.value = 'min'; 
    statusFilter.value = [];
    typeFilter.value = ['footage', 'informational'];
    sortBy.value = [{ key: 'displayDate', order: 'desc' }, { key: 'timestamp', order: 'desc' }];
  } else if (currentView.value === 'agencyResources') {
    if(unsubscribeAgencies) unsubscribeAgencies(); 
    loadAgencyResourcesData(db); 
    agencySearchQuery.value = ''; 
    areaSearchQuery.value = ''; 
    agencyStateFilter.value = 'All'; 
    agencyStatusFilter.value = [];
    agencySortBy.value = [{ key: 'hotStatus', order: 'desc' }, { key: 'requests', order: 'desc' }];
  } else if (currentView.value === 'research') { 
    if(unsubscribeResearch) unsubscribeResearch(); 
    loadResearchData(db); 
    sourceSearchQuery.value = ''; 
    agencyStateFilter.value = 'All'; 
    researchSortBy.value = [{ key: 'dateReviewDone', order: 'desc' }]; 
  } 
}

function viewAssociatedRecords(agencyName) { if (!firebaseServices.value?.db) return; agencyNameForAssociatedRecords.value = agencyName; associatedRecords.value = allRequests.value.filter( r => getAgencyDisplayName(r).toLowerCase() === agencyName.toLowerCase() ); associatedRecordsDialog.value = true; }
function openDeleteConfirmDialog(type) { if (type === 'record') itemToDelete.value = form.value; else if (type === 'agency') itemToDelete.value = agencyForm.value; else if (type === 'research') itemToDelete.value = researchForm.value; if (itemToDelete.value) { deleteType.value = type; deleteConfirmDialog.value = true; } }
async function initiateDelete() {
  if (!itemToDelete.value || !itemToDelete.value.id || !firebaseServices.value?.db) return;
  dialog.value = false; agencyDialog.value = false; researchDialog.value = false; deleteConfirmDialog.value = false;
  const itemType = deleteType.value;
  let collectionName = '';
  if (itemType === 'record') collectionName = 'CCMSheetsDATA';
  else if (itemType === 'agency') collectionName = 'AgencyInfo';
  else if (itemType === 'research') collectionName = 'researchRecords';
  const db = firebaseServices.value.db;
  const docId = itemToDelete.value.id;
  try {
    const docRef = doc(db, collectionName, docId);
    // Snapshot the raw Firestore doc (real Timestamps, no UI-mapped fields
    // like displayDate/requestedOn) so undoDelete can restore it verbatim.
    const snap = await getDoc(docRef);
    if (!snap.exists()) { showSnackbar('Item not found — it may have already been deleted.', 'error'); return; }
    deleteUndo.track({ type: itemType, item: { id: snap.id, ...snap.data() } });
    await deleteDoc(docRef);
    showSnackbar('Item deleted', 'success', 5000, true);
  } catch (error) { console.error("Error deleting item:", error); logError('deleteItem', error); deleteUndo.clear(); }
}

async function cancelNewAgency() { newAgencyConfirmDialog.value = false; isSavingRecord.value = false; }
async function confirmAndSaveNewAgency() {
  if (!firebaseServices.value?.db) return;
  newAgencyConfirmDialog.value = false;
  isSavingRecord.value = true;
  try {
    const cleanName = (newAgencyName.value || '').trim();
    const nameLower = cleanName.toLowerCase();
    // Guard against duplicates: the snapshot listener may not have synced yet,
    // or the agency may already exist under different casing.
    const alreadyExists = agencyResourcesData.value.some(
      a => (a.agencyName || '').trim().toLowerCase() === nameLower
    );
    if (!alreadyExists) {
      const newAgency = { agencyName: cleanName, state: form.value?.state || '', userId: viewAsUserId.value, timestamp: serverTimestamp() };
      await addDoc(collection(firebaseServices.value.db, 'AgencyInfo'), newAgency);
    }
    if (form.value) {
      // skipAgencyCheck: the agency now exists in Firestore even if the local
      // snapshot hasn't updated — re-checking here caused duplicate agencies.
      await handleSaveRecord(form.value, { skipAgencyCheck: true });
    } else {
      isSavingRecord.value = false;
    }
  } catch (error) {
    console.error('Error creating agency:', error);
    logError('createAgency', error); showSnackbar('Could not create the new agency. Please try again.', 'error');
    isSavingRecord.value = false;
  }
}
function applySorting(data, sortConfig) { if (!sortConfig || sortConfig.length === 0) return data; const sortedData = [...data]; sortedData.sort((a, b) => { for (const sortItem of sortConfig) { const key = sortItem.key; const order = sortItem.order || 'asc'; let valA = a[key]; let valB = b[key]; if (valA === null || valA === undefined) return 1; if (valB === null || valB === undefined) return -1; let comparison = 0; if (typeof valA === 'string' && typeof valB === 'string') { comparison = valA.localeCompare(valB, undefined, { numeric: true }); } else if (valA < valB) { comparison = -1; } else if (valA > valB) { comparison = 1; } if (comparison !== 0) { return order === 'desc' ? comparison * -1 : comparison; } } return 0; }); return sortedData; }

function generateCSV() { 
  if (currentView.value === 'docket') { if (docketViewRef.value) { docketViewRef.value.exportToCSV(); } return; }
  let dataToExport, sortConfig, columns; 
  if (currentView.value === 'records') { 
    dataToExport = displayedRequests.value; 
    sortConfig = sortBy.value; 
    columns = [ 
        { key: 'name', header: 'Name' }, 
        { key: 'recordType', header: 'Type' },
        { key: 'agencyName', header: 'Agency' }, 
        { key: 'displayDate', header: 'Request Date' }, 
        { key: 'stage', header: 'Stage' }, 
        { key: 'howFound', header: 'How Found' }, 
        { key: 'cost', header: 'Cost' }, 
        { key: "subjectMatter", header: "Subject Matter" }, 
        { key: 'misc', header: 'Misc.' }, 
        { key: 'update', header: 'Update' }, 
        { key: 'otherInfo', header: 'Other Info' }, 
        { key: 'quoteDate', header: 'Quote Date' }, 
        { key: 'paidDate', header: 'Paid Date' }, 
        { key: 'receivedDate', header: 'Received Date' },
        { key: 'bullpenDate', header: 'Bullpen Date' }, 
        { key: 'checkDate', header: 'Status Check Date' } 
    ]; 
  } else if (currentView.value === 'agencyResources') { 
    dataToExport = displayedAgencyResources.value; 
    sortConfig = agencySortBy.value; 
    columns = [ { key: 'agencyName', header: 'Agency Name' }, { key: 'state', header: 'State' }, { key: 'hotStatusLabel', header: 'Rating' }, { key: 'area', header: 'Area' }, { key: 'agencyContact', header: 'Contact' }, { key: 'email', header: 'Email' }, { key: 'contactInfo', header: 'Contact Info' }, { key: 'court', header: 'Court/Source' }, { key: 'miscInfo', header: 'Agency Info'} ]; 
  } else if (currentView.value === 'research') { 
    dataToExport = displayedResearchRecords.value; 
    sortConfig = researchSortBy.value; 
    columns = [ { key: 'agencyName', header: 'Agency Name' }, { key: 'state', header: 'State' }, { key: 'area', header: 'Area' }, { key: 'source', header: 'Source' }, { key: 'dateReviewDone', header: 'Date Reviewed' }, { key: 'charges', header: 'Charges' }, { key: 'dateRangeStart', header: 'Date Range Start'}, { key: 'dateRangeEnd', header: 'Date Range End'}, { key: 'noRequestsMade', header: 'No Requests Made'}, { key: 'nextSteps', header: 'Next Steps' }, { key: 'notes', header: 'Notes' } ]; 
  } 
  if (!dataToExport || dataToExport.length === 0) return; 
  const sortedData = applySorting(dataToExport, sortConfig);
  const RATING_LABELS = { 2: 'Hot', 1: 'Good', 0: 'Standard', 3: 'Cool', 4: 'Avoid' };
  const processedData = sortedData.map(item => { 
    const baseItem = {}; 
    columns.forEach(col => { 
      if (col.key === 'hotStatusLabel') { baseItem[col.key] = RATING_LABELS[item.hotStatus] ?? ''; }
      else if (col.key === 'recordType') { baseItem[col.key] = getRecordType(item) === 'informational' ? 'Info' : 'Video'; }
      else if (currentView.value === 'records' && col.key === 'agencyName') { baseItem[col.key] = getAgencyDisplayName(item); } 
      else if (item[col.key] instanceof Date) { baseItem[col.key] = formatDate(item[col.key]); } 
      else if (typeof item[col.key] === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(item[col.key])) { baseItem[col.key] = formatDate(item[col.key]); }
      else { baseItem[col.key] = item[col.key] ?? ''; } 
    }); 
    // Guard against CSV formula injection on every string cell
    Object.keys(baseItem).forEach(k => { if (typeof baseItem[k] === 'string') baseItem[k] = csvSafeCell(baseItem[k]); });
    return baseItem; 
  }); 
  try { 
    const csvString = stringify(processedData, { header: true, columns: columns }); 
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' }); 
    const link = document.createElement("a"); 
    const url = URL.createObjectURL(blob); 
    link.setAttribute("href", url); 
    // Prefix follows the SIGNED-IN account (see accountBranding): the owner's
    // exports keep the copcammax_ name, client accounts get an unbranded one.
    // Signed-in is the right key here — it's about who receives the file, so
    // impersonating a client to pull their data still lands a copcammax_ file
    // on the owner's machine, which the client never sees.
    const exportPrefix = accountBranding.value.exportPrefix;
    link.setAttribute("download", `${exportPrefix}_${currentView.value}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden'; 
    document.body.appendChild(link); link.click(); document.body.removeChild(link); 
  } catch (error) { console.error("CSV error:", error); } 
}

function handleNavigateToAgencies(state) { setView('agencyResources'); agencySearchQuery.value = ''; agencyStateFilter.value = state; }

// --- UPGRADED FILTER LOGIC ---
function exportForAI() {
  const records = allRequests.value.filter(r => !r.trash);
  const agencies = agencyResourcesData.value;
  if (!records.length && !agencies.length) { showSnackbar('Nothing to export.', 'warning'); return; }

  const RATING_LABELS = { 2: 'Hot', 1: 'Good', 0: 'Standard', 3: 'Cool', 4: 'Avoid' };
  const fmt = v => (v instanceof Date ? formatDate(v) : (v ?? ''));

  // Sheet 1 - Requests (one row per record)
  const requestRows = records.map(r => ({
    'Request Date': fmt(r.displayDate),
    'Agency': getAgencyDisplayName(r),
    'State': r.state ?? '',
    'Stage': r.stage ?? '',
    'Record Name': r.name ?? '',
    'Subject Matter': r.subjectMatter ?? '',
    'How Found': r.howFound ?? '',
    'Cost': r.cost ?? '',
    'Rating': r.rating ?? '',
    'Notes': r.notes ?? '',
    'Update': r.update ?? '',
    'Misc': r.misc ?? '',
    'Other Info': r.otherInfo ?? '',
    'Quote Date': fmt(r.quoteDate),
    'Paid Date': fmt(r.paidDate),
    'Received Date': fmt(r.receivedDate),
    'Bullpen Date': fmt(r.bullpenDate),
    'Status Check Date': fmt(r.checkDate),
    // Why a Kill (or Bullpen rated <=1.5) didn't work -- up to 3 chips,
    // comma-separated. ccm-ai's export_ai_analysis.py mirrors this column set.
    'Kill Reason': r.killReason ?? '',
    'Kill Note': r.killNote ?? '',
  }));

  // Sheet 2 - Agencies (one row per agency, full detail incl. rating)
  const agencyRows = agencies.map(a => ({
    'Agency Name': getAgencyDisplayName(a),
    'State': a.state ?? '',
    'Rating': RATING_LABELS[a.hotStatus] ?? '',
    'Area': a.area ?? '',
    'Contact': a.agencyContact ?? '',
    'Email': a.email ?? '',
    'Contact Info': a.contactInfo ?? '',
    'Court/Source': a.court ?? '',
    'Agency Info': a.miscInfo ?? '',
  }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(requestRows), 'Requests');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(agencyRows), 'Agencies');

  const today = new Date().toISOString().split('T')[0];
  XLSX.writeFile(wb, `ccm-ai-analysis-${today}.xlsx`);
  showSnackbar(`Exported ${records.length} records and ${agencies.length} agencies.`, 'success');
}

// Lowercased search text per record object. Records keep their identity across
// snapshots unless they changed, so this is computed once per edit, not per keystroke.
const searchBlobCache = new WeakMap();
function getSearchBlob(r) {
  let blob = searchBlobCache.get(r);
  if (blob === undefined) {
    blob = [r.name, getAgencyDisplayName(r), r.stage, r.howFound, r.subjectMatter, r.misc, r.update, r.otherInfo, r.cost]
      .filter(v => v !== null && v !== undefined).join(' ').toLowerCase();
    searchBlobCache.set(r, blob);
  }
  return blob;
}
function filterData() { 
  if (!firebaseServices.value?.db) return; 
  let data = [...allRequests.value]; 
  
  if (searchQuery.value) { 
    const { includeTerms, excludeTerms } = parseSearchWithNegation(searchQuery.value); 
    data = data.filter(r => { 
      const searchableText = (r.name || '');
      const lowerText = searchableText.toLowerCase();

      const hasAllIncludes = includeTerms.every(term => lowerText.includes(term)); 
      const hasNoExcludes = !excludeTerms.some(term => lowerText.includes(term)); 
      return hasAllIncludes && hasNoExcludes; 
    }); 
  } 

  if (toolbarSearchQuery.value) {
    const { includeTerms, excludeTerms } = parseSearchWithNegation(toolbarSearchQuery.value); 
    data = data.filter(r => { 
      const searchableValues = getSearchBlob(r);

      const hasAllIncludes = includeTerms.every(term => searchableValues.includes(term)); 
      const hasNoExcludes = !excludeTerms.some(term => searchableValues.includes(term)); 
      return hasAllIncludes && hasNoExcludes; 
    }); 
  }
  
  if (agencySearchQuery.value) { 
    const { includeTerms, excludeTerms } = parseSearchWithNegation(agencySearchQuery.value); 
    data = data.filter(r => { 
      const searchableText = getAgencyDisplayName(r)?.toLowerCase() || ''; 
      const hasAllIncludes = includeTerms.every(term => searchableText.includes(term)); 
      const hasNoExcludes = !excludeTerms.some(term => searchableText.includes(term)); 
      return hasAllIncludes && hasNoExcludes; 
    }); 
  } 
  
  if (stageFilter.value?.length > 0) { 
    data = data.filter(r => stageFilter.value.includes(String(r.stage || 'Pending'))); 
  } 
  
  if (stageExcludeFilter.value?.length > 0) {
    data = data.filter(r => !stageExcludeFilter.value.includes(String(r.stage || 'Pending')));
  }

  if (agencyStateFilter.value !== 'All') { 
    data = data.filter(r => { 
      const agency = agencyByNameMap.value.get(getAgencyDisplayName(r).toLowerCase()); 
      return agency?.state === agencyStateFilter.value; 
    }); 
  } 

  if (recordRatingFilter.value > 0) {
    if (recordRatingMode.value === 'exact') {
       data = data.filter(r => r.rating === recordRatingFilter.value);
    } else {
       data = data.filter(r => (r.rating || 0) >= recordRatingFilter.value);
    }
  }

  if (statusFilter.value && statusFilter.value.length > 0) {
    data = data.filter(r => {
      const checks = [];
      if (statusFilter.value.includes('action')) checks.push(r.isActionItem);
      if (statusFilter.value.includes('unknown')) checks.push(r.rating === -1);
      return checks.some(c => c === true);
    });
  }

  data = data.filter(r => typeFilter.value.includes(getRecordType(r)));

  displayedRequests.value = data;
}

function filterAgencyResources() { 
  if (!firebaseServices.value?.db) return; 
  let data = [...agencyResourcesData.value]; 

  if (agencySearchQuery.value) { 
    const { includeTerms, excludeTerms } = parseSearchWithNegation(agencySearchQuery.value); 
    data = data.filter(r => { 
      const searchableText = (r.agencyName || '').toLowerCase(); 
      const hasAllIncludes = includeTerms.every(term => searchableText.includes(term)); 
      const hasNoExcludes = !excludeTerms.some(term => searchableText.includes(term)); 
      return hasAllIncludes && hasNoExcludes; 
    }); 
  } 

  if (areaSearchQuery.value) { 
    const { includeTerms, excludeTerms } = parseSearchWithNegation(areaSearchQuery.value); 
    data = data.filter(r => { 
      const searchableText = (r.area || '').toLowerCase(); 
      const hasAllIncludes = includeTerms.every(term => searchableText.includes(term)); 
      const hasNoExcludes = !excludeTerms.some(term => searchableText.includes(term)); 
      return hasAllIncludes && hasNoExcludes; 
    }); 
  } 

  if (agencyStateFilter.value !== 'All') { 
    data = data.filter(r => r.state === agencyStateFilter.value); 
  }

  if (agencyStatusFilter.value && agencyStatusFilter.value.length > 0) {
    data = data.filter(r => {
      const status = r.hotStatus !== undefined ? r.hotStatus : 0; 
      return agencyStatusFilter.value.includes(status);
    });
  }

  if (toolbarSearchQuery.value) {
    const { includeTerms, excludeTerms } = parseSearchWithNegation(toolbarSearchQuery.value); 
    data = data.filter(r => { 
      const searchableValues = [
        r.agencyName,
        r.state,
        r.area,
        r.agencyContact,
        r.email,
        r.contactInfo,
        r.court,
        r.miscInfo
      ].filter(v => v !== null && v !== undefined).join(' ').toLowerCase();

      const hasAllIncludes = includeTerms.every(term => searchableValues.includes(term)); 
      const hasNoExcludes = !excludeTerms.some(term => searchableValues.includes(term)); 
      return hasAllIncludes && hasNoExcludes; 
    }); 
  }

  displayedAgencyResources.value = data; 
}

function filterResearchRecords() {
  if (!firebaseServices.value?.db) return;
  let data = [...researchRecords.value];
  if (sourceSearchQuery.value) data = data.filter(r => r.source?.toLowerCase().includes(sourceSearchQuery.value.toLowerCase()));
  if (agencyStateFilter.value !== 'All') data = data.filter(r => r.state === agencyStateFilter.value);
  if (toolbarSearchQuery.value) {
    const { includeTerms, excludeTerms } = parseSearchWithNegation(toolbarSearchQuery.value);
    data = data.filter(r => {
      const searchableValues = [
        r.agencyName,
        r.state,
        r.area,
        r.source,
        r.charges,
        r.noRequestsMade,
        r.nextSteps,
        r.notes
      ].filter(v => v !== null && v !== undefined).join(' ').toLowerCase();

      const hasAllIncludes = includeTerms.every(term => searchableValues.includes(term));
      const hasNoExcludes = !excludeTerms.some(term => searchableValues.includes(term));
      return hasAllIncludes && hasNoExcludes;
    });
  }
  displayedResearchRecords.value = data;
}
function debounce(fn, delay) { let timeoutId; return function(...args) { clearTimeout(timeoutId); timeoutId = setTimeout(() => { fn.apply(this, args); }, delay); }; }
const debounceFilterData = debounce(filterData, 300); const debounceFilterAgencyResources = debounce(filterAgencyResources, 300); const debounceFilterResearchRecords = debounce(filterResearchRecords, 300);

async function initializeUserNotes(db) {
  if (!viewAsUserId.value || !db) return;
  const notesCollectionRef = collection(db, 'users', viewAsUserId.value, 'notes');
  try {
    const noteSnap = await getDoc(doc(notesCollectionRef, 'general'));
    if (!noteSnap.exists()) {
      const batch = writeBatch(db);
      const defaultNotes = {
        general: { title: 'General Note', color: '#BBDEFB', content: [{ title: 'Tab 1', content: '' }] },
        agency: { title: 'Agency Info', color: '#B2DFDB', content: [{ title: 'Tab 1', content: '' }] },
        extra: { title: 'Research', color: '#F3E5F5', content: [{ title: 'Tab 1', content: '' }] },
        research: { title: 'Templates/Forms', color: '#FFCCBC', content: [{ title: 'Tab 1', content: '' }] }
      };
      for (const [noteId, noteData] of Object.entries(defaultNotes)) {
        batch.set(doc(notesCollectionRef, noteId), noteData);
      }
      await batch.commit();
    }
  } catch (error) { console.error("Error notes init:", error); }
}

// Notes are only shown on the Notes tab, so their listener (and the default-
// notes bootstrap read) start on first visit instead of at login.
let notesStarted = false;
function ensureNotes(db) {
  if (notesStarted || !db || !viewAsUserId.value) return;
  notesStarted = true;
  initializeUserNotes(db);
  setupNotesListener(db);
}
// Called when the signed-in user (or viewed user) changes: drop the old
// listener and restart only if the Notes tab has already been opened.
function resetNotes(db) {
  if (unsubscribeNotes) { unsubscribeNotes(); unsubscribeNotes = null; }
  notesStarted = false;
  notes.value = [];
  if (visitedViews.has('notes')) ensureNotes(db);
}
function setupNotesListener(db) { 
  if (!viewAsUserId.value || !db) return; 
  if(unsubscribeNotes) unsubscribeNotes(); 
  unsubscribeNotes = onSnapshot(collection(db, 'users', viewAsUserId.value, 'notes'), (querySnapshot) => { 
    const userNotes = []; 
    querySnapshot.forEach((doc) => { 
      userNotes.push({ id: doc.id, ...doc.data() }); 
    }); 
    notes.value = userNotes; 
  }); 
}

function updateComboboxOptions() {
  const recencyMap = new Map();
  for (const record of allRequests.value) {
    if (record.howFound && record.timestamp) {
      // Defensive: some records have a corrupted timestamp field (a plain
      // {seconds, nanoseconds} object instead of a real Firestore Timestamp)
      // left over from a past undoDelete bug — .toDate() would throw on those.
      const currentTimestamp = record.timestamp?.toDate
        ? record.timestamp.toDate()
        : (record.timestamp?.seconds !== undefined ? new Date(record.timestamp.seconds * 1000) : null);
      if (!currentTimestamp) continue;
      const existingTimestamp = recencyMap.get(record.howFound);
      if (!existingTimestamp || currentTimestamp > existingTimestamp) { recencyMap.set(record.howFound, currentTimestamp); }
    }
  }
  howFoundOptions.value = Array.from(recencyMap.entries()).sort((a, b) => b[1] - a[1]).map(entry => entry[0]);
}
function updateChargesOptions() { chargesOptions.value = Array.from(new Set(researchRecords.value.map(r => r.charges).filter(Boolean))).sort(); }
function updateSourceOptions() { sourceOptions.value = Array.from(new Set(researchRecords.value.map(r => r.source).filter(Boolean))).sort(); }
// id -> mapped record from the previous snapshot (see loadFirestoreData).
let recordCache = new Map();
function loadFirestoreData(db) {
  if (!db || !viewAsUserId.value) return;
  recordCache = new Map();
  if(unsubscribeRecords) unsubscribeRecords();
  recordsLoading.value = true;
  unsubscribeRecords = onSnapshot(
    query(collection(db, 'CCMSheetsDATA'), where("userId", "==", viewAsUserId.value)),
    (querySnapshot) => {
      try {
        // Incremental: only re-map documents Firestore reports as changed and
        // reuse the previous object for the rest. A single write used to
        // rebuild all ~2,500 records (and defeat the per-record search cache).
        const changedIds = new Set(querySnapshot.docChanges().map(c => c.doc.id));
        const nextCache = new Map();
        allRequests.value = querySnapshot.docs.map(doc => {
          const prev = recordCache.get(doc.id);
          if (prev && !changedIds.has(doc.id)) { nextCache.set(doc.id, prev); return prev; }
          const data = doc.data();
          const rec = { id: doc.id, ...data, displayDate: data.requestedOnFixed?.toDate ? data.requestedOnFixed.toDate() : null, quoteDate: data.quoteDate?.toDate ? data.quoteDate.toDate() : null, paidDate: data.paidDate?.toDate ? data.paidDate.toDate() : null, receivedDate: data.receivedDate?.toDate ? data.receivedDate.toDate() : null, bullpenDate: data.bullpenDate?.toDate ? data.bullpenDate.toDate() : null, checkDate: data.checkDate?.toDate ? data.checkDate.toDate().toISOString().slice(0, 10) : (data.checkDate ?? null) };
          nextCache.set(doc.id, rec);
          return rec;
        });
        recordCache = nextCache;
        // The Record modal takes a one-time copy of its record on open
        // (editRecord() below), so an external write while it's still open —
        // create_folder.py's or the email automation's stage/rating/date
        // write-back (CLAUDE.md) — would otherwise never reach it, and the
        // modal's own Save would silently overwrite that write-back with the
        // stale pre-write values. Re-normalize `form` here so RecordEditDialog's
        // props.item watcher sees a fresh copy for the SAME record id and can
        // merge in whatever the user hasn't already started editing. Skipped
        // when nothing actually changed, so an unrelated record's write
        // doesn't needlessly re-trigger the dialog's watcher.
        if (dialog.value && form.value?.id) {
          const liveRecord = allRequests.value.find(r => r.id === form.value.id);
          if (liveRecord) {
            const normalized = normalizeRecordForForm(liveRecord);
            if (!deepEqual(normalized, form.value)) {
              form.value = normalized;
            }
          }
        }
        updateComboboxOptions();
        filterData();
      } catch (error) {
        console.error('loadFirestoreData: error processing snapshot', error);
        logError('loadFirestoreData', error);
      } finally {
        recordsLoading.value = false;
      }
      runAutoGhostSweep(db);
    },
    (error) => {
      console.error('loadFirestoreData: listener error', error);
      logError('loadFirestoreData', error);
      recordsLoading.value = false;
    }
  );
}

// --- Auto-Ghost Sweep ---
// Once per 24 hours, flip Pending FOOTAGE records that are over the resolved
// ghost threshold (state override, else global 150 days — see
// @/utils/ghostThresholds.js) old with no recent (60-day) status check to
// the Ghosted stage.
// STRICT MODE: blank checkDate does NOT protect the record (the user
// chose strict over lenient — see project docs).
//
// Safeguards:
//   - Runs ONLY when viewing the user's own data (never during admin impersonation).
//   - Throttled to once per 24h via localStorage key (per-user).
//   - Each flipped record gets a "[auto-ghosted YYYY-MM-DD: ...]" note appended
//     to the Update field so the change is auditable and reversible.
//   - Action stage records are NEVER auto-flipped (active back-and-forth).
//   - Informational records (ARs/Arrests/Reports/etc) are excluded.
const GHOST_STALE_CHECK_DAYS = 60;

async function runAutoGhostSweep(db) {
  try {
    // Never auto-flip another user's records during admin impersonation
    if (!user.value || viewAsUserId.value !== user.value.uid) return;
    if (!allRequests.value.length) return;

    const throttleKey = `autoGhostLastRun:${user.value.uid}`;
    const lastRun = parseInt(localStorage.getItem(throttleKey) || '0', 10);
    const now = Date.now();
    if (now - lastRun < 24 * 60 * 60 * 1000) return; // 24h throttle

    const todayMs = now;
    const cutoffStaleCheckMs = todayMs - GHOST_STALE_CHECK_DAYS * 86400000;

    const candidates = allRequests.value.filter(r => {
      if (r.stage !== 'Pending') return false; // Pending only (not Action)
      if (getRecordType(r) === 'informational') return false;
      const reqMs = r.displayDate ? r.displayDate.getTime() : null;
      if (!reqMs) return false;
      // Threshold is per-record now: agency override -> state override ->
      // global default (150), instead of one flat number for everyone. (3.1b)
      const cutoffRequestMs = todayMs - resolveGhostThreshold(r.agencyName, r.state) * 86400000;
      if (reqMs > cutoffRequestMs) return false;
      // STRICT: blank checkDate does NOT protect the record
      const checkMs = r.checkDate?.toDate ? r.checkDate.toDate().getTime()
                    : (r.checkDate ? new Date(r.checkDate).getTime() : null);
      if (checkMs && checkMs > cutoffStaleCheckMs) return false;
      return true;
    });

    // Always record the run timestamp, even if 0 candidates (don't rescan all day)
    localStorage.setItem(throttleKey, String(now));

    if (!candidates.length) return;

    const isoDate = new Date().toISOString().slice(0, 10);
    // Firestore batch limit is 500 writes — chunk if needed
    for (let i = 0; i < candidates.length; i += 400) {
      const chunk = candidates.slice(i, i + 400);
      const batch = writeBatch(db);
      chunk.forEach(r => {
        const ageDays = Math.floor((todayMs - r.displayDate.getTime()) / 86400000);
        const note = `[auto-ghosted ${isoDate}: no activity for ${ageDays} days]`;
        const update = r.update ? `${r.update}\n${note}` : note;
        batch.update(doc(db, 'CCMSheetsDATA', r.id), { stage: 'Ghosted', update });
      });
      await batch.commit();
    }

    showSnackbar(`Auto-ghosted ${candidates.length} stale Pending record${candidates.length === 1 ? '' : 's'}.`, 'info', 10000, false, true);
  } catch (error) {
    console.error('Auto-ghost sweep failed:', error);
    logError('autoGhostSweep', error, { recordCount: allRequests.value.length });
    // Don't show error to user — sweep is best-effort, not critical
  }
}
function loadAgencyResourcesData(db) { if (!db || !viewAsUserId.value) return; if(unsubscribeAgencies) unsubscribeAgencies(); agenciesLoading.value = true; unsubscribeAgencies = onSnapshot(query(collection(db, 'AgencyInfo'), where("userId", "==", viewAsUserId.value)), (querySnapshot) => { agencyResourcesData.value = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })); filterAgencyResources(); agenciesLoading.value = false; }); }
function loadResearchData(db) { if (!db || !viewAsUserId.value) return; if(unsubscribeResearch) unsubscribeResearch(); researchLoading.value = true; unsubscribeResearch = onSnapshot(query(collectionGroup(db, 'researchRecords'), where("userId", "==", viewAsUserId.value)), (querySnapshot) => { researchRecords.value = querySnapshot.docs.map(doc => { const data = doc.data(); return { id: doc.id, ...data, dateRangeStart: data.dateRangeStart?.toDate ? data.dateRangeStart.toDate() : null, dateRangeEnd: data.dateRangeEnd?.toDate ? data.dateRangeEnd.toDate() : null, dateReviewDone: data.dateReviewDone?.toDate ? data.dateReviewDone.toDate() : null }; }); updateChargesOptions(); updateSourceOptions(); filterResearchRecords(); researchLoading.value = false; }); }
function loadDocketData(db) { if (!db || !viewAsUserId.value) return; if (unsubscribeDocket) unsubscribeDocket(); docketLoading.value = true; unsubscribeDocket = onSnapshot(query(collection(db, 'docketItems'), where("userId", "==", viewAsUserId.value)), (snapshot) => { docketItems.value = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })); docketLoading.value = false; }); }

watch(selectedUserId, (newUserId) => { if (newUserId && firebaseServices.value?.db && isAdminUser.value) { const db = firebaseServices.value.db; loadFirestoreData(db); loadAgencyResourcesData(db); loadResearchData(db); resetNotes(db); loadDocketData(db); } });
watch(searchQuery, debounceFilterData); 

// Combined stageFilter watcher for conditional Rating sort. Only forces the
// rating sort when transitioning INTO the Bullpen-only state (Bullpen and/or
// BullpenX), and restores whatever sort was active before that (not a
// hardcoded default) on the way out, so clearing stage chips never clobbers
// a user-chosen sort.
let preBullpenSort = null;
let inBullpenOverride = false;
watch(stageFilter, (newStages) => {
  const isBullpenOnly = newStages.length > 0 && newStages.every(s => s === 'Bullpen' || s === 'BullpenX');

  if (isBullpenOnly && !inBullpenOverride) {
    preBullpenSort = [...sortBy.value];
    sortBy.value = [
      { key: 'rating', order: 'desc' },
      { key: 'displayDate', order: 'desc' }
    ];
    inBullpenOverride = true;
  } else if (!isBullpenOnly && inBullpenOverride) {
    sortBy.value = preBullpenSort ?? sortBy.value;
    preBullpenSort = null;
    inBullpenOverride = false;
  }

  filterData();
});

watch(stageExcludeFilter, filterData); watch(areaSearchQuery, debounceFilterAgencyResources); watch(sourceSearchQuery, debounceFilterResearchRecords); watch(agencySearchQuery, () => { if (currentView.value === 'records') debounceFilterData(); else if (currentView.value === 'agencyResources') debounceFilterAgencyResources(); }); watch(agencyStateFilter, () => { if (currentView.value === 'records') filterData(); else if (currentView.value === 'agencyResources') filterAgencyResources(); else if (currentView.value === 'research') filterResearchRecords(); });
watch(agencyStatusFilter, filterAgencyResources);
watch(recordRatingFilter, filterData); 
watch(recordRatingMode, filterData); 
watch(statusFilter, filterData);
watch(typeFilter, filterData);
watch(toolbarSearchQuery, () => {
  if (currentView.value === 'records') debounceFilterData();
  else if (currentView.value === 'agencyResources') debounceFilterAgencyResources();
  else if (currentView.value === 'research') debounceFilterResearchRecords();
});

function openDocketAddDialog(agencyName) { 
  docketAddInitialAgency.value = (typeof agencyName === 'string') ? agencyName : '';
  docketAddDialog.value = true; 
}

function handleAIReportAddDocket({ agencyName }) {
  openDocketAddDialog(agencyName);
}

async function saveNewDocketItem(data) {
  if (!firebaseServices.value?.db) return;
  isSavingDocket.value = true;
  try { 
    const cleanData = sanitizeDocketItem(data);
    await addDoc(collection(firebaseServices.value.db, 'docketItems'), { 
      ...cleanData, 
      userId: viewAsUserId.value, 
      timestamp: serverTimestamp(), 
      archived: false 
    }); 
    docketAddDialog.value = false; 
    showSnackbar('Item added to docket.');
  } 
  catch (error) { 
    console.error("Error docket save:", error); 
    logError('addToDocket', error); showSnackbar('Couldn\'t add the item to the docket. Check your connection and try again.', 'error');
  } 
  finally { isSavingDocket.value = false; }
}

async function updateDocketItem(item) {
  if (!firebaseServices.value?.db) return;
  const isSilent = !!item.silent;
  try { 
    const itemRef = doc(firebaseServices.value.db, 'docketItems', item.id);
    const dataToSave = sanitizeDocketItem(item); // silent is stripped here

    await updateDoc(itemRef, { 
      ...dataToSave, 
      timestamp: serverTimestamp() 
    }); 
    if (!isSilent) showSnackbar('Docket item saved successfully!');
  } 
  catch (error) { 
    console.error("Error docket update:", error); 
    logError('saveDocketItem', error); showSnackbar('Couldn\'t save the docket item. Check your connection and try again.', 'error');
  }
}

async function deleteDocketItem(item) {
  if (!firebaseServices.value?.db || !item.id) return;
  deleteType.value = 'docket';
  try {
    const docRef = doc(firebaseServices.value.db, 'docketItems', item.id);
    // Snapshot the raw doc (item here is enriched with computed fields like
    // hotStatus/dueDate/state from filteredItems) so undo restores verbatim.
    const snap = await getDoc(docRef);
    if (!snap.exists()) { showSnackbar('Item not found — it may have already been deleted.', 'error'); return; }
    deleteUndo.track({ type: 'docket', item: { id: snap.id, ...snap.data() } });
    await deleteDoc(docRef);
    showSnackbar('Docket item deleted permanently', 'info', 5000, true);
  }
  catch (error) { console.error("Error docket delete:", error); logError('deleteDocketItem', error); deleteUndo.clear(); }
}
function openResetDialog(item) { itemToReset.value = item; docketResetDialog.value = true; }

async function confirmResetDocketItem({ newDuration, notes, customDate, extensions }) {
  if (!firebaseServices.value?.db || !itemToReset.value) return;
  isResettingDocket.value = true;
  try {
    const itemRef = doc(firebaseServices.value.db, 'docketItems', itemToReset.value.id);
    const tsDate = customDate ? Timestamp.fromDate(new Date(customDate)) : serverTimestamp();
    const finalDuration = customDate ? 'Custom' : newDuration;
    const finalCustomDate = customDate ? tsDate : null;

    await updateDoc(itemRef, {
      startDate: tsDate,
      baseDuration: finalDuration,
      extensions: extensions || [],
      notes: notes,
      customDate: finalCustomDate 
    });
    
    docketResetDialog.value = false;
    itemToReset.value = null;
    showSnackbar('Docket timer reset successfully');
  } catch (error) { 
    console.error("Reset Error:", error); 
    logError('resetDocketTimer', error); showSnackbar('Couldn\'t reset the docket timer. Check your connection and try again.', 'error');
  } 
  finally { isResettingDocket.value = false; }
}

async function handleEmptyTrash(items) {
  if (!items || items.length === 0) return;
  if (!firebaseServices.value?.db) return;
  
  try {
    const batch = writeBatch(firebaseServices.value.db);
    items.forEach(item => {
      const docRef = doc(firebaseServices.value.db, 'docketItems', item.id);
      batch.delete(docRef);
    });
    await batch.commit();
    showSnackbar(`${items.length} items permanently deleted.`, 'success');
  } catch (error) {
    console.error("Error emptying trash:", error);
    showSnackbar("Error emptying trash.", 'error');
  }
}

async function generateAIAnalysis() {
  isAnalyzing.value = true;
  try {
    const functions = getFunctions();
    const analyzeCall = httpsCallable(functions, 'analyzeAgencies');
    
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
    
    const recentRecords = allRequests.value
      .filter(r => r.displayDate && new Date(r.displayDate) >= ninetyDaysAgo)
      .map(r => ({
        agency: getAgencyDisplayName(r) || "Unknown",
        stage: r.stage || "Pending",
        cost: r.cost || 0
      }));

    const result = await analyzeCall({ records: recentRecords });
    aiAnalysisData.value = result.data;
  } catch (error) {
    console.error("Analysis Error:", error);
    logError('aiAnalysis', error); showSnackbar('AI analysis didn\'t complete. Check your connection and try again in a moment.', 'error');
  } finally {
    isAnalyzing.value = false;
  }
}

</script>

<style scoped>
:deep(.v-data-table-footer) { padding-top: 0; padding-bottom: 0; }
.top-right-actions { gap: 4px; }
.account-avatar-btn img { opacity: 0.7; transition: opacity 0.2s ease; }
.account-avatar-btn:hover img { opacity: 1; }
.dialog-content-scrollable { height: 75vh; overflow-y: auto; }
.note-modal-tabs-container { min-height: 64px; display: flex; align-items: center; border-bottom: 1px solid #e0e0e0; }
:deep(.analytical-background.v-main) { background-color: #F5F5F5 !important; }
.zoom-on-hover { transition: transform 0.2s ease-in-out; }
.zoom-on-hover:hover { transform: scale(1.05); }
/* 30px height, tighter horizontal padding, font-size untouched (no `size` prop change) */
.nav-btn-compact { --v-btn-height: 30px; padding: 0 8px; }
.table-link { cursor: pointer; color: #1976D2; text-decoration: none; }
.table-link:hover { text-decoration: underline; }
.modal-close-button { position: absolute; top: 10px; right: 10px; color: white; background-color: #f44336 !important; border-radius: 4px; min-width: 36px; height: 36px; padding: 0; box-shadow: none; }
.modal-close-button-note { color: white; background-color: #f44336 !important; border-radius: 4px; min-width: 36px; height: 36px; padding: 0; box-shadow: none; }
.v-card-title { background-color: #1976D2; color: white; padding: 16px; font-size: 1.25rem; font-weight: 500; position: relative; }
.note-modal-title span { color: black; text-shadow: none; }
.styled-table :deep(tbody tr:nth-child(even)) { background-color: #f0f8ff; }
</style>